/**
 * @file CreateCustomerOrder.js
 * @version 2.34.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-17
 * @modified 2026-11-03
 * @description Create customer orders — one compact header row and an editable
 *              line grid. Customer detail lives behind the eye button; recent
 *              orders live in the Item picker.
 *
 * 2.34.0 (2026-11-03, Cam — "Typed lines should require user to input sales
 * and purchase account codes when orders are created"): a typed line (no
 * inventory item) shows a Sales account and a Purchase account under its
 * item (OrderLinesGrid `requireAccounts`, lists from /api/inventory/sales-
 * and purchase-accounts). A new or changed typed line needs its sales account,
 * and its purchase account when it is bought on a PO (a supplier, not stock)
 * — outlined, flagged on the line, and Save greyed with the reason
 * (utils/lineAccountRequired; the server refuses it too, 400
 * ACCOUNT_REQUIRED). Before migrations/order_line_account_codes.sql the
 * pickers are greyed "Not ready" and nothing is asked (GET
 * /api/orders/line-accounts).
 *
 * 2.33.0 (2026-11-01, Cam — an invoice that had gone out): after the save
 * the customer may be sent the corrected copy (CorrectedSendDialog, the
 * save's `corrected_send_offer`): statement and invoices (the default),
 * the invoice only, or not now. A paid invoice's flag is said in the save's
 * message.
 *
 * 2.32.0 (2026-10-30, Cam — the invoice and Xero's lock): an invoiced order's
 * units correction asks first what its invoice becomes (InvoiceProposalDialog,
 * 409 INVOICE_CHANGES_PROPOSED — "Apply these changes and save"), and the
 * prior-month question (usePriorMonthConfirm). An invoice behind Xero's lock
 * (409 UNITS_INVOICE_LOCKED) keeps everything typed: the edit is held for this
 * tab (usePersistedState 'createCustomerOrder.heldEdits', by order) and comes
 * back when the order is opened again; the window offers "Check the lock
 * again" (POST /api/admin/xero-lock/refresh, then the save again).
 *
 * 2.31.0 (2026-10-30, Cam — SO-D10442: "I need to be able to change the units
 * on a delivered customer order, which affects the weight and pallets"): on a
 * delivered order a line's Units / Weight / Pallets are corrected — the
 * quantity stays, and the line asks, in its own place, what its price was per
 * (the old unit — the default, converted — the new unit, or typed where
 * nothing converts); its price and cost follow (utils/afterDelivery
 * applyUnitsCorrections, the server's utils/unitsAfterDelivery). The save
 * sends each corrected line's answer (`units_basis`); the server works the
 * figures out itself and carries the units to the PO-D and PO-H lines.
 * Lines the server says cannot be corrected (`units_correction`) have their
 * units greyed with why.
 *
 * 2.21.0 (2026-10-09, Cam): a Delivered or INVOICED order opens for editing
 * (an invoiced one used to be refused). Words, item codes, the supplier,
 * delivery terms and haulier can be corrected; the quantity cells, adding and
 * removing lines — and on an invoiced order its prices, tax and additives,
 * the customer and the delivery date — are greyed with the reason
 * (utils/afterDelivery, the server's utils/afterDeliveryEdit). Its POs are
 * corrected in place for our records (no revised PO offered); an invoiced
 * order is then offered the matching invoice changes (InvoiceEditOfferDialog).
 * A line may still be added (no delivered PO is joined); a corrected item's
 * contract link is dropped by the server (price kept). The reason is
 * optional — a field in the banner instead of a required dialog.
 *
 * 2.22.0 (2026-10-15, Cam): from a standing order, a hand-off naming an
 * archived product is refused on arrival (re-checked against the standing
 * order as it is now — the hand-off may be stale) with the Standing Orders
 * screen's words; the create sends `standing_order_id`, and the server refuses
 * an archived line on it too (409 STANDING_ORDER_PRODUCT_ARCHIVED). Archived
 * alternatives arrive already left out, with the note saying so
 * (utils/standingOrderProducts).
 * 2.23.1 (2026-10-21, Cam): the "Update the purchase order?" question says
 * units and pallets follow too, and a changed unit re-costs the PO line per its
 * new unit (routes/orders.js buildPoLineItem). The grid shows a blank Unit
 * Cost as the item's cost per the line's unit and asks what an item's cost is
 * per when the item has no unit (OrderLinesGrid 2.19.0).
 * 2.24.0 (2026-10-22, Cam): a third-party order from our stock shows
 * "Collected" beside its Delivery Date — read only, blank on creation, the
 * day the haulier collected it once recorded (Collection / Delivery). A
 * collected order not yet delivered says so in the banner.
 *
 * 2.25.0 (2026-10-23, Cam — "Please don't allow 0 in qty when creating
 * customer order"): a line at a quantity of 0 (blank, or below 0) is flagged
 * on the line and the save is greyed with the reason until it has one — a new
 * line, or one changed to 0 on an edit; a line already at 0 that the edit
 * leaves alone still saves (utils/orderLineQuantity; the server refuses the
 * same, 400 ZERO_QUANTITY).
 *
 * 2.27.0 (2026-10-28, Cam — "make units a mandatory field when creating
 * customer orders"): a line with no known unit — none of its own and none on
 * its item — has its Units cell outlined and a flag, and the save is greyed
 * with the reason until it has one; picking an item puts its unit on the line.
 * A new line, or one whose item, quantity or units change; an old line left
 * alone still saves (utils/lineUnitRequired; the server refuses the same,
 * 400 UNIT_REQUIRED).
 *
 * 2.26.0 (2026-10-26, Cam): under Third-Party Haulier, a note says whether
 * the order is planned on the transport board — its haulier supplier linked
 * to a haulier in Transport Settings → Hauliers, and a PO-D — or what would
 * put it there ("Link <supplier> to a haulier in Transport Settings to plan
 * it on the board"). GET /api/orders/third-party-board.
 *
 * 2.29.0 (2026-10-30, Cam — rep order requests, 29 Oct): opened from Sales
 * Orders' "Rep requests" (utils/repOrderRequestApi hand-off). A request for an
 * order fills the new order in — customer, delivery date (a range's first day;
 * the range goes in the notes), notes, each line's item or typed words,
 * additives, quantity and unit — once the item list is loaded; a change to an
 * order shows what the rep asked above Edit Customer Order. Either way a
 * banner says it, and the save carries `rep_request_id`, which completes the
 * request in the same transaction. One already done meanwhile is refused
 * (409 REP_REQUEST_DONE, nothing saved) and the save is offered without it.
 *
 * 2.30.0 (2026-10-30, Cam — the Item picker's X): each "Recently ordered" row
 * can be taken off this customer's list for everyone until it is ordered
 * again, with Undo (hooks/usePreviousItemRemoval).
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import { resolveLineForItem, itemPurchaseTextOf } from '../utils/customerPricing';
import { wordsOnItemPick } from '../utils/pickedItemWords';
import { toISODate } from '../utils/periodHelper';
import { useAccess } from '../hooks/useAccess';
import OrderLinesGrid, { blankLine, lineIsEmpty, lineTotals, LineTotalsPanel } from './OrderLinesGrid';
import CustomerNotesPanel from './CustomerNotesPanel';
import SendToSupplierFlow from './SendToSupplierFlow';
import { useNavStore } from '../store/navStore';
import { revealInSearch } from '../utils/revealInSearch';
import InventoryItemFormModal from './InventoryItemFormModal';
import './CreateCustomerOrder.css';
import './TableStyles.css';
import SearchableDropdown from './SearchableDropdown';
import { SUPPLIER_DELIVERY_TERMS_OPTIONS, DEFAULT_OUR_DELIVERY_TERMS, THIRD_PARTY_SUPPLIER_TERMS, THIRD_PARTY_OUR_TERMS, DIRECT_DELIVERY_TERMS, HAULAGE_SERVICE_OPTIONS, suggestedHaulageService, ourDeliveryTermsOptions, pairOnChoose } from '../utils/deliveryTerms';
import { lineUnitsFromSaved } from '../utils/units';
import { isNoWeight, linesMissingPallets } from '../utils/lineUnits';
import { archivedLinesOf, archivedRefusalMessage } from '../utils/standingOrderProducts';
import useDateRepricing from '../hooks/useDateRepricing';
import DatePriceChangesDialog from './DatePriceChangesDialog';
import DateInput from './DateInput';
import AlertDialog from './AlertDialog';
import { showAlert } from './Alert';
import ConfirmDialog from './ConfirmDialog';
import Dropdown from './Dropdown';
import Textarea from './Textarea';
// LoadingSpinner available if needed
import ContactModal from './ContactModal';
import { formatCurrency } from '../utils/currencyFormatter';
// getCurrentPeriodId available via periodHelper if needed
import { useSlideOutButton } from './SlideOutButton';
import { lineQuantityRefusals, quantityRefusalMessage, quantityProblemText } from '../utils/orderLineQuantity';
import { lineUnitRefusals, unitRefusalMessage, UNIT_PROBLEM_TEXT } from '../utils/lineUnitRequired';
import { orderLineAccountRefusals, accountRefusalMessage, SALES_ACCOUNT_PROBLEM_TEXT, PURCHASE_ACCOUNT_PROBLEM_TEXT, ORDER_LINE_ACCOUNTS_NOT_READY } from '../utils/lineAccountRequired';
import { isStockSupplier } from '../utils/stockSupplier';
import InvoiceEditOfferDialog from './InvoiceEditOfferDialog';
import InvoiceProposalDialog from './InvoiceProposalDialog';
import CorrectedSendDialog from './CorrectedSendDialog';
import usePriorMonthConfirm, { isPriorMonthConfirm } from '../hooks/usePriorMonthConfirm';
import usePersistedState from '../hooks/usePersistedState';
import { isInvoiceQuestion, applyAnswer } from '../utils/invoiceProposals';
import { UNITS_LOCK_CODE } from '../utils/unitsAfterDelivery';
import { orderEditState, orderClosedCells, orderBannerText, hasInvoiceOffer, applyUnitsCorrections, unitsCorrectionFlags, unitsBasisFor } from '../utils/afterDelivery';
import { takeHandOff, handOffWaiting } from '../utils/repOrderRequestApi';
import { allowsAdditives } from '../utils/accountCodes';
import { orderPrefill, amendChanges, lineText, deliveryText, requestRef, KIND_WORDS } from '../utils/repOrderRequests';
import './RepOrderRequests.css';
import usePreviousItemRemoval from '../hooks/usePreviousItemRemoval';

/**
 * What a re-sent PO's email says changed. A lone delivery-date change names the
 * new date in the subject; anything else is "amended", with the list in the
 * message (SendPOEmailModal textFor).
 */
function revisionFor(changes = []) {
  const list = (changes || []).filter(Boolean);
  const date = list.length === 1 && /^delivery date /.test(list[0]) ? list[0].split('→').pop().trim() : null;
  const pretty = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : iso);
  return { summary: date ? `new delivery date ${pretty(date)}` : 'amended', changes: list };
}

function CreateCustomerOrder({ user, onNavigate }) {
  // parseAddressWithLineBreaks available if needed for address display

  const getAddressLine1 = (addressData) => {
    if (!addressData) return '';
    if (typeof addressData === 'object') {
      return addressData.addressLine1 || '';
    }
    if (typeof addressData === 'string' && addressData.startsWith('{')) {
      try {
        const addressObj = JSON.parse(addressData);
        return addressObj.addressLine1 || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  };
  // State for customer selection
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  // State for contracts and inventory
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [customerContracts, setCustomerContracts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [, setLoadingContracts] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  // A typed line's own accounts (2.34.0): the lists for its pickers, and
  // whether the order lines can keep them yet (null = not asked yet).
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  const [lineAccounts, setLineAccounts] = useState({ ready: false, reason: null, checked: false });

  // State for delivery details
  const [deliveryDate, setDeliveryDate] = useState('');
  // Optional alternative delivery days (each with a preference; ties allowed).
  // The primary delivery date above stays the committed day; these are extra
  // candidate days that also make a contract price available if any falls in range.
  const [optionDates, setOptionDates] = useState([]); // [{ date, preference }]
  // We collect from the supplier and deliver to the customer, unless told otherwise.
  // No default: the supplier's terms are chosen on every order (Cam, 2026-09-29) —
  // a default nobody read is how the wrong terms reach a purchase order.
  const [supplierDeliveryTerms, setSupplierDeliveryTerms] = useState('');
  const [excelAgriDeliveryTerms, setExcelAgriDeliveryTerms] = useState(DEFAULT_OUR_DELIVERY_TERMS);
  // Third-party haulage: the haulier, a supplier contact. Only asked for, and
  // only sent, on Third-Party Delivery terms.
  const [haulierAccountId, setHaulierAccountId] = useState('');
  const isThirdParty = excelAgriDeliveryTerms === THIRD_PARTY_OUR_TERMS;
  // The service the haulier is booked for. Suggested from the delivery date
  // (Saturday / Next Day / Dedicated Day) until the user picks one — after
  // that it is theirs and the date no longer moves it.
  const [haulageService, setHaulageService] = useState('');
  const haulageServiceChosenRef = useRef(false);
  useEffect(() => {
    if (!isThirdParty || haulageServiceChosenRef.current) return;
    setHaulageService(suggestedHaulageService(deliveryDate));
  }, [isThirdParty, deliveryDate]);
  // After saving a third-party order, and after any PO-D has been dealt with,
  // the PO-H is offered: { orderId, orderNumber }.
  const [pendingHaulage, setPendingHaulage] = useState(null);
  // Direct Delivery: the supplier delivers and charges us haulage per pallet,
  // by the customer's postcode. { postcode, recognised, rates: { supplierId:
  // { rate, rate_id, prefix, effective_from } } } for the current customer,
  // date and suppliers, or null when not looked up.
  const isDirectDelivery = supplierDeliveryTerms === DIRECT_DELIVERY_TERMS;
  const [haulageLookup, setHaulageLookup] = useState(null);
  const [haulagePrompt, setHaulagePrompt] = useState(null);
  const [raisingHaulage, setRaisingHaulage] = useState(false);
  // The delivery date each price list was last loaded for. The re-pricing
  // check after a date change waits until all three match the new date.
  const [pricingLoadedFor, setPricingLoadedFor] = useState({ contracts: null, spot: null, milkPowder: null });
  const [notes, setNotes] = useState('');

  // Every candidate delivery day for pricing: primary + option dates, deduped.
  const candidateDates = [...new Set([deliveryDate, ...optionDates.map(o => o.date)].filter(Boolean))];
  // The named pricing period a given date falls in (from the loaded periods).
  // Compared as YYYY-MM-DD strings: period boundaries are plain dates, and
  // comparing Date objects put local midnight against Postgres's UTC midnight,
  // which under BST placed a date sitting exactly on a period's start_date one
  // hour outside its own period.
  const periodNameForDate = (d) => {
    const day = toISODate(d);
    if (!day) return null;
    const p = periods.find(pp => {
      const start = toISODate(pp.start_date);
      const end = toISODate(pp.end_date);
      return (!start || start <= day) && (!end || end >= day);
    });
    return p ? p.name : null;
  };

  // Customer pricing, all loaded for every candidate delivery day and then
  // narrowed to the delivery date itself when it matters — see
  // utils/customerPricing. The grid's Item picker resolves a line's price from
  // these three, in that order of specificity.
  const [customerSpotPrices, setCustomerSpotPrices] = useState([]);
  const [, setLoadingSpotPrices] = useState(false);
  const [milkPowderContracts, setMilkPowderContracts] = useState([]);

  // UI state
  const [, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

  // Duplicate mode. A duplicate is a NEW order that happens to start life
  // looking like an old one: no SO number, no PO links, nothing carried over
  // that belongs to the original. `duplicatedFrom` is only the number we
  // duplicated, kept so the screen and the save prompt can name it.
  const [duplicatedFrom, setDuplicatedFrom] = useState(null);
  // What the form looked like the moment the copy landed, so save can tell
  // whether anything was actually changed.
  const duplicateBaselineRef = useRef(null);

  /*
   * Where to go when this screen is done. An edit opened from a PO — "you
   * meant the customer order" (LinkedPOEditPrompt) — goes back to Search
   * Purchase Orders, reopening the PO window if that is where it began;
   * everything else goes back to Search Customer Orders, as it always has.
   * Read here (not removed — StrictMode runs initialisers twice) and cleared
   * by the mount effect below.
   */
  const [returnTo] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' && sessionStorage.getItem('editOrderId')
        ? sessionStorage.getItem('editOrderReturn') : null;
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const leave = () => {
    if (!onNavigate) return;
    if (returnTo?.menu) {
      if (returnTo.poModalId) useNavStore.getState().setPage('searchPOs.poModalId', returnTo.poModalId);
      onNavigate(returnTo.menu);
    } else {
      onNavigate('search-customer-orders');
    }
  };

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  // Third-party haulage on the transport board (Cam, 26 Oct 2026): the note
  // under Third-Party Haulier — { tone, text } or null.
  const [thirdPartyBoardHint, setThirdPartyBoardHint] = useState(null);
  useEffect(() => {
    if (!isThirdParty || !haulierAccountId) { setThirdPartyBoardHint(null); return undefined; }
    let cancelled = false;
    const params = new URLSearchParams({ accountId: String(haulierAccountId) });
    if (editingOrderId) params.set('orderId', String(editingOrderId));
    fetch(`${API_URL}/api/orders/third-party-board?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setThirdPartyBoardHint(d && d.success ? d.hint || null : null); })
      .catch(() => { if (!cancelled) setThirdPartyBoardHint(null); });
    return () => { cancelled = true; };
  }, [isThirdParty, haulierAccountId, editingOrderId]);
  // Captured once on mount (before the edit-mode effect clears the flag): true
  // for a fresh create, so we can auto-open the customer picker in that case only.
  const [autoOpenCustomer] = useState(() => typeof window !== 'undefined' && !sessionStorage.getItem('editOrderId') && !handOffWaiting());
  const [associatedPOs, setAssociatedPOs] = useState([]);
  // The order already has purchase orders: saving rebuilds them to match —
  // the customer included, whose name and delivery address the POs carry.
  const [hasLinkedPOs, setHasLinkedPOs] = useState(false);
  // The customer the order was saved with, so saving can say it is changing.
  const originalCustomerIdRef = useRef('');
  // Some of the order has been delivered: an edit needs a reason, and its
  // quantities are recorded with Add Supplied Quantity instead.
  const [orderDelivered, setOrderDelivered] = useState(false);
  // Third-party from our stock: the day the haulier collected it (read only here).
  const [collection, setCollection] = useState({ collectsFromStock: false, collectedDate: null, deliveryStatus: null });
  // Delivered (status) or invoiced: corrections only (utils/afterDelivery).
  const [afterDelivery, setAfterDelivery] = useState({ delivered: false, invoiced: false, any: false });
  // The save's invoice offer for an invoiced order, then what comes next.
  const [invoiceOffer, setInvoiceOffer] = useState(null);
  // What became of the invoice, added to the save's closing message.
  const invoiceNoteRef = useRef(null);
  const originalQtyRef = useRef(new Map());
  // The order's lines as loaded (an edit) — what units-required compares with.
  const originalUnitLinesRef = useRef(new Map());
  // After delivery (2.31.0): each line as loaded — units, price, cost — what a
  // units correction is worked from; and the server's per-line blocks.
  const originalLinesRef = useRef(new Map());
  const [unitsCorrection, setUnitsCorrection] = useState(null);
  // 2.32.0: the invoice's units offer; an edit held while Xero's lock is on
  // (by order id, this tab); the lock refusal's window.
  const [unitsInvoiceQuestion, setUnitsInvoiceQuestion] = useState(null);
  const [heldEdits, setHeldEdits] = usePersistedState('createCustomerOrder.heldEdits', {});
  const [heldRestored, setHeldRestored] = useState(null);
  const [lockRefusal, setLockRefusal] = useState(null);
  const [checkingLock, setCheckingLock] = useState(false);
  // 2.33.0: the corrected copy for the customer (an invoice that had gone out).
  const [correctedSend, setCorrectedSend] = useState(null);
  const priorMonth = usePriorMonthConfirm();
  // Why a delivered / invoiced order is being changed — optional (2026-10-09:
  // the audit trail records every change), typed in the banner, kept when given.
  const [editReasonText, setEditReasonText] = useState('');
  const editReasonRef = useRef('');
  // The standing order this new order is being made from (sent with the
  // create, so the server can refuse an archived product too).
  const standingOrderIdRef = useRef(null);
  // A rep's request this order is made from, or the change it asks for (Sales
  // Orders' "Rep requests"): shown in a banner, sent with the save.
  const [repRequest, setRepRequest] = useState(null);
  const repRequestRef = useRef(null);
  // A request's lines wait for the item list (the words and tax come from it).
  const pendingRepLinesRef = useRef(null);

  // PO generation modal state (post-creation)
  /* After the purchase orders are raised, the obvious next thing is to send
     them — and the moment the screen navigates away, doing so costs a
     search. Asked here, while the order is still on screen. */
  const [sendPrompt, setSendPrompt] = useState(null);
  const [sendFlow, setSendFlow] = useState(null);

  const [poModal, setPoModal] = useState({
    isOpen: false,
    orderId: null,
    orderNumber: '',
    supplierGroups: [],
    selectedSuppliers: new Set(),
    generating: false
  });

   // State for order lines
  const [orderLines, setOrderLines] = useState([]);

  // Cost is a permission, not a preference — hide the column outright rather
  // than showing an empty one, which reads as "no cost recorded".
  const { can } = useAccess();
  const canViewCosts = can('costs.view');

  /*
   * Supplier haulage on a Direct Delivery order. Looked up whenever what it
   * depends on changes — customer (their postcode), delivery date (rates are
   * dated), and the set of suppliers on the lines — then written onto every
   * line that has not had a figure typed over it. The server looks the same
   * rate up again on save, so this is the screen showing what will be stored,
   * not the only source of it. Not looked up for someone who cannot see costs:
   * the field is hidden from them and the server fills it in.
   */
  const haulageSupplierKey = [...new Set(orderLines.map(l => l.supplier_contact_id).filter(Boolean))].sort().join(',');
  useEffect(() => {
    if (!isDirectDelivery || !canViewCosts || !selectedCustomerId) {
      setHaulageLookup(null);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      account_id: selectedCustomerId,
      date: deliveryDate || '',
      supplier_ids: haulageSupplierKey
    });
    fetch(`${API_URL}/api/supplier-haulage/resolve?${params}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data?.success) return;
        setHaulageLookup({ postcode: data.postcode, recognised: data.recognised, rates: data.rates || {} });
        setOrderLines(prev => {
          let changed = false;
          const next = prev.map(line => {
            if (line.haulage_overridden) return line;
            const found = line.supplier_contact_id ? data.rates?.[line.supplier_contact_id] : null;
            const rate = found ? Number(found.rate).toFixed(2) : '';
            if ((line.haulage_rate ?? '') === rate) return line;
            changed = true;
            return { ...line, haulage_rate: rate };
          });
          return changed ? next : prev;
        });
      })
      .catch(() => { if (!cancelled) setHaulageLookup({ error: true, rates: {} }); });
    return () => { cancelled = true; };
  }, [isDirectDelivery, canViewCosts, selectedCustomerId, deliveryDate, haulageSupplierKey]);

  // Items this customer has bought before, most recent first, from their own
  // invoices and orders. This feeds both the Item picker's "Recently ordered"
  // panel and its "Search customer's previous items" toggle.
  //
  // The panel used to come from a free-text purchase-order search on the
  // account number, which matched that number anywhere in any PO — so account
  // 0081 was shown item 2404213 because another customer's PO carried the
  // reference SO_1008119. History is looked up by account id now, never by text.
  const [previousItems, setPreviousItems] = useState([]);
  // The picker's X: take a row off this customer's "Recently ordered" for
  // everyone until it is ordered again (Cam, 30 Oct 2026), with Undo.
  const { removePrevious, toast: previousItemToast } = usePreviousItemRemoval({
    side: 'customer',
    accountId: selectedCustomerId,
    accountName: customers.find(c => String(c.id) === String(selectedCustomerId))?.company_name || '',
    previousItems,
    setPreviousItems,
  });

  // Creating an item from inside the Item picker: remember which row asked, so
  // the new item lands on it rather than making the user find it again.
  const [addItemForRow, setAddItemForRow] = useState(null);
  const handleOpenAddItem = (rowIndex) => setAddItemForRow(rowIndex);

  // The order has a sequence — customer, then delivery date, then lines — and
  // nothing is hidden to enforce it. A field you cannot see teaches you
  // nothing; a field you can see, greyed, with a sentence saying what to do
  // first, teaches the sequence on the first try and then gets out of the way.
  const needsCustomer = !selectedCustomerId;
  const needsDeliveryDate = !!selectedCustomerId && !deliveryDate;
  const deliveryFieldsHint = needsCustomer ? 'Choose a customer first.' : undefined;
  const lineFieldsHint = needsCustomer
    ? 'Choose a customer first, then a delivery date.'
    : (needsDeliveryDate ? 'Choose a delivery date first — it decides which contract prices apply.' : undefined);

  // Lines that already have a purchase order behind them. They are editable —
  // the grid tags them "PO raised", and the confirmation at save says which POs
  // will be rebuilt.
  // No line at a quantity of 0 (Cam, 23 Oct 2026): flagged on the line, the
  // save greyed with the reason. An edit leaves a line already at 0 alone.
  const qtyRefusals = lineQuantityRefusals(orderLines, editMode ? originalQtyRef.current : null);
  const qtyRefusalText = quantityRefusalMessage(qtyRefusals);
  // Every line has a unit (Cam, 28 Oct 2026): a new or changed line with
  // none of its own and none on its item is flagged, the save greyed.
  const unitRefusals = lineUnitRefusals(orderLines, editMode ? originalUnitLinesRef.current : null,
    line => (line.inventory_item_id ? inventoryItems.find(i => i.id === line.inventory_item_id) || null : null));
  const unitRefusalText = unitRefusalMessage(unitRefusals);
  // A typed line names its own accounts (2.34.0): its sales account, and its
  // purchase account when it is bought on a PO (a supplier, not stock).
  const accountRefusals = lineAccounts.ready
    ? orderLineAccountRefusals(orderLines, editMode ? originalUnitLinesRef.current : null, {
      bought: (l) => !!l.supplier_contact_id && !isStockSupplier(suppliers.find(x => x.id === l.supplier_contact_id))
    })
    : [];
  const accountRefusalText = accountRefusalMessage(accountRefusals, 'order');
  const filledLine = (r) => orderLines[r.index] && (orderLines[r.index].inventory_item_id || String(orderLines[r.index].product_name || '').trim());
  const accountFlagText = (r) => (r.missing.length === 2
    ? 'Choose its Sales account and Purchase account under the item — a line with no item has nothing to take them from'
    : (r.missing[0] === 'sales' ? SALES_ACCOUNT_PROBLEM_TEXT : PURCHASE_ACCOUNT_PROBLEM_TEXT));
  const qtyLineFlags = qtyRefusals.length || unitRefusals.length || accountRefusals.length
    ? [...qtyRefusals.filter(filledLine).map(r => [r.id, { key: 'zero-qty', tone: 'error', icon: 'error', text: quantityProblemText(r.problem) }]),
      ...unitRefusals.filter(filledLine).map(r => [r.id, { key: 'no-unit', tone: 'error', icon: 'error', text: UNIT_PROBLEM_TEXT }]),
      ...accountRefusals.map(r => [r.id, { key: 'no-account', tone: 'error', icon: 'error', text: accountFlagText(r) }])]
      .reduce((acc, [id, flag]) => ({ ...acc, [id]: [...(acc[id] || []), flag] }), {})
    : null;
  // After delivery (2.31.0): a corrected line's note and its question — what
  // was the price per? — in its own place, with the choice.
  const itemOfLine = (line) => (line?.inventory_item_id ? inventoryItems.find(i => i.id === line.inventory_item_id) || null : null);
  const setLinesCorrected = (next) => setOrderLines(prev => applyUnitsCorrections(
    typeof next === 'function' ? next(prev) : next, originalLinesRef.current, itemOfLine, { canViewCosts }));
  const patchLine = (lineId, changes) => setLinesCorrected(prev => prev.map(l => (l.id === lineId ? { ...l, ...changes } : l)));
  const unitsFlagsRaw = editMode && afterDelivery.any ? unitsCorrectionFlags(orderLines, originalLinesRef.current, itemOfLine) : {};
  const unitsFlags = Object.fromEntries(Object.entries(unitsFlagsRaw).map(([id, flags]) => [id, flags.map(f => {
    const c = f.unitsChoice;
    const line = orderLines.find(l => String(l.id) === String(id));
    const typed = c.choices.length === 1 && c.choices[0].basis === 'typed';
    return {
      key: f.key, tone: f.tone, icon: f.icon,
      text: [f.text, c.money ? `→ ${c.money}.` : '', c.problem ? `${c.problem}.` : ''].filter(Boolean).join(' '),
      actions: typed ? [] : c.choices.map(ch => ({
        key: ch.basis,
        icon: (c.basis || 'old') === ch.basis ? 'radio_button_checked' : 'radio_button_unchecked',
        label: `${ch.label}${ch.detail ? ` → ${ch.detail}` : ''}`,
        title: 'What this line\'s price was per — the price, the line\'s cost and its PO line\'s cost follow',
        onClick: () => patchLine(line.id, { units_basis: ch.basis })
      })),
      control: typed ? (
        <span className="units-typed-price">
          <label>Price per the new unit £
            <input type="number" min="0" step="0.0001" value={line?.units_typed_price ?? ''}
              onChange={(e) => patchLine(line.id, { units_basis: 'typed', units_typed_price: e.target.value })} />
          </label>
          {canViewCosts && (
            <label>Cost £
              <input type="number" min="0" step="0.0001" value={line?.units_typed_cost ?? ''}
                placeholder="kept"
                onChange={(e) => patchLine(line.id, { units_basis: 'typed', units_typed_cost: e.target.value })} />
            </label>
          )}
        </span>
      ) : null
    };
  })]));
  const unitsBasisProblem = Object.values(unitsFlagsRaw).some(flags => flags.some(f => f.unitsChoice.problem));
  const allLineFlags = qtyLineFlags || Object.keys(unitsFlags).length
    ? Object.fromEntries([...new Set([...Object.keys(qtyLineFlags || {}), ...Object.keys(unitsFlags)])]
      .map(id => [id, [...((qtyLineFlags || {})[id] || []), ...(unitsFlags[id] || [])]]))
    : null;
  const saveRefusalText = [qtyRefusalText, unitRefusalText, accountRefusalText,
    unitsBasisProblem ? 'Say what each corrected line\'s price was per (type it where the units can\'t be converted).' : '']
    .filter(Boolean).join(' ');

  const lockedLineIds = useMemo(() => {
    const ids = new Set();
    for (const line of orderLines) {
      if (line.purchase_order_id || line.purchase_order_number) ids.add(line.id);
    }
    return ids;
  }, [orderLines]);
  useSlideOutButton([orderLines.length, poModal.isOpen]);

  // Delivery date (or, on an edit, the customer) changed with lines already
  // priced: offer the new prices. Asked only once every price list has
  // reloaded for it.
  const pricingKey = `${selectedCustomerId}|${deliveryDate}`;
  const repricing = useDateRepricing({
    variant: 'order',
    lines: orderLines,
    setLines: setOrderLines,
    date: deliveryDate,
    // Keyed by customer AND date: a changed customer is offered its own
    // prices only once they have loaded (noteCustomerChange).
    ready: pricingLoadedFor.contracts === pricingKey
      && pricingLoadedFor.spot === pricingKey
      && pricingLoadedFor.milkPowder === pricingKey,
    customerId: selectedCustomerId,
    pricing: {
      contracts: customerContracts,
      spotPrices: customerSpotPrices,
      milkPowderContracts,
      suppliers,
      periodName: periodNameForDate(deliveryDate),
    },
    inventoryItems,
    includeCost: canViewCosts,
  });

  // Load initial data
  useEffect(() => {
    loadCustomers();
    loadPeriods();
    // Product lists are loaded by the customer-scope effect below, which also
    // fires on mount — loading them here too would just double the fetch.
    loadSuppliers();
    loadLineAccounts();

    // Check if we're in edit mode
    const editOrderId = sessionStorage.getItem('editOrderId');
    if (editOrderId) {
      setEditMode(true);
      setEditingOrderId(editOrderId);
      sessionStorage.removeItem('editOrderId');
      sessionStorage.removeItem('editOrderReturn');
      loadOrderForEdit(editOrderId);
    }

    // Or duplicating one. Deliberately NOT edit mode: the same form, loaded the
    // same way, but saved as a new order — so it POSTs, gets its own SO number
    // and its own PO prompt.
    const duplicateOrderId = sessionStorage.getItem('duplicateOrderId');
    if (duplicateOrderId) {
      sessionStorage.removeItem('duplicateOrderId');
      loadOrderForEdit(duplicateOrderId, { duplicate: true });
    }

    // From a rep's request (Sales Orders' "Rep requests"). A new order is
    // filled in from it; a change is shown above the order being edited — only
    // when it is that order.
    const handed = takeHandOff();
    if (handed && (handed.kind === 'order' ? !editOrderId : String(handed.order_id) === String(editOrderId))) {
      repRequestRef.current = handed;
      setRepRequest(handed);
      if (handed.kind === 'order') {
        const p = orderPrefill(handed);
        if (p.account_id) setSelectedCustomerId(String(p.account_id));
        if (p.delivery_date) setDeliveryDate(p.delivery_date);
        setNotes(p.notes || '');
        pendingRepLinesRef.current = p.lines;
      }
    }

    // Check if we're creating from a standing order
    const standingOrderData = sessionStorage.getItem('standingOrderData');
    if (standingOrderData) {
      sessionStorage.removeItem('standingOrderData');
      loadFromStandingOrder(JSON.parse(standingOrderData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load customer-specific periods when customer changes
  useEffect(() => {
    if (selectedCustomerId) {
      loadCustomerPeriods(selectedCustomerId);
    } else {
      // Reset to all periods when no customer selected
      loadPeriods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  // Additives apply only to compound feed milled to order (sales accounts 201
  // and 207), never to stock lines such as 200M / 200N. The grid decides this
  // per row from that row's own item, so there is no screen-level "selected
  // product" to derive it from any more.

  // A rep's request's lines, once the item list is here: each item's words
  // and tax as picking it would give, the rep's unit on the line (units are
  // required), quantity and additives; a typed product as typed. Unpriced —
  // the office prices it as any order.
  useEffect(() => {
    const pending = pendingRepLinesRef.current;
    if (!pending || !inventoryItems.length) return;
    pendingRepLinesRef.current = null;
    setOrderLines(pending.map((l) => {
      const item = l.item_id ? inventoryItems.find(i => i.id === l.item_id) : null;
      const base = blankLine();
      const words = item ? wordsOnItemPick('order', base, item) : {};
      return {
        ...base,
        inventory_item_id: item ? item.id : null,
        product_code: item ? item.product_code : '',
        product_name: item ? (words.product_name || item.product_name) : (l.text || l.sales_text || ''),
        ...(item ? { purchase_description: words.purchase_description || '' } : {}),
        quantity: l.quantity === null || l.quantity === undefined ? '' : String(l.quantity),
        tax_type: item ? (item.tax_type || null) : null,
        account_code: item ? (item.sales_account_code || '') : '',
        ...lineUnitsFromSaved({ unit_weight_kg: l.unit_weight_kg, qty_per_pallet: null, pallets: null }),
        // Only where the order line may carry them (compounds — the grid's own
        // rule); the banner still says what the rep asked for.
        additives: item && allowsAdditives(item.sales_account_code)
          ? (l.additives || []).map(a => ({
            additive_id: a.additive_id, additive_code: a.additive_code, additive_name: a.additive_name, additive_price: null
          }))
          : []
      };
    }));
  }, [inventoryItems]);

  // Re-scope the item list whenever the customer changes: which products a
  // customer may be offered depends on who they are, so the list loaded on
  // mount (unscoped) has to be replaced once one is picked — and replaced again
  // if the user switches to a different customer.
  useEffect(() => {
    loadInventoryItems();
    loadPreviousItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  // Open a fresh order with one empty row. A grid with no rows at all reads as
  // "nothing to do here"; a row waiting to be filled reads as "start here".
  // Never on an edit, where the order's own lines are about to arrive.
  useEffect(() => {
    if (!selectedCustomerId || editMode || duplicatedFrom) return;
    setOrderLines(prev => (prev.length === 0 ? [blankLine()] : prev));
  }, [selectedCustomerId, editMode, duplicatedFrom]);

  // Load customer contracts when customer or period changes
  useEffect(() => {
    if (selectedCustomerId && selectedPeriod) {
      loadCustomerContracts();
    } else {
      setCustomerContracts([]);
      setPricingLoadedFor(p => ({ ...p, contracts: pricingKey }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, selectedPeriod, deliveryDate, optionDates]);

  // Load customer spot prices + milk powder contracts when customer, delivery
  // date or the option dates change (all are effective-at-date lookups that now
  // consider every candidate date).
  useEffect(() => {
    if (selectedCustomerId && candidateDates.length) {
      loadCustomerSpotPrices();
      loadMilkPowderContracts();
    } else {
      setCustomerSpotPrices([]);
      setMilkPowderContracts([]);
      setPricingLoadedFor(p => ({ ...p, spot: pricingKey, milkPowder: pricingKey }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, deliveryDate, optionDates]);

  // Load customer details when customer is selected
  useEffect(() => {
    if (selectedCustomerId) {
      loadCustomerDetails();
    } else {
      setCustomerDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  // Handle delivery terms interaction logic
  useEffect(() => {
    if (supplierDeliveryTerms === 'Direct Delivery') {
      // Auto-select Direct Delivery for Excel Agri and it becomes locked
      setExcelAgriDeliveryTerms('Direct Delivery');
    } else if (supplierDeliveryTerms === THIRD_PARTY_SUPPLIER_TERMS) {
      // Third-party terms are a pair: the haulier collects AND delivers.
      setExcelAgriDeliveryTerms(THIRD_PARTY_OUR_TERMS);
    } else {
      // If Excel Agri is currently set to Direct Delivery (or third-party) but
      // the supplier side changed away from it, reset it to Delivery
      if (excelAgriDeliveryTerms === 'Direct Delivery' || excelAgriDeliveryTerms === THIRD_PARTY_OUR_TERMS) {
        setExcelAgriDeliveryTerms('Delivery');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierDeliveryTerms]);

  // …and the other way round: our side on third-party puts the supplier's there
  // too, and leaving it takes the supplier's back to the default.
  useEffect(() => {
    if (excelAgriDeliveryTerms === THIRD_PARTY_OUR_TERMS) {
      if (supplierDeliveryTerms !== THIRD_PARTY_SUPPLIER_TERMS) setSupplierDeliveryTerms(THIRD_PARTY_SUPPLIER_TERMS);
    } else if (supplierDeliveryTerms === THIRD_PARTY_SUPPLIER_TERMS) {
      setSupplierDeliveryTerms('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excelAgriDeliveryTerms]);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCustomers(data.contacts || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=10000`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSuppliers(data.contacts || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  // A typed line's accounts (2.34.0): the sales and purchase lists, and
  // whether the order lines can keep them yet.
  const loadLineAccounts = async () => {
    const get = async (url) => {
      try {
        const r = await fetch(url, { credentials: 'include' });
        return await r.json();
      } catch (error) {
        return null;
      }
    };
    const [sales, purchase, state] = await Promise.all([
      get(`${API_URL}/api/inventory/sales-accounts`),
      get(`${API_URL}/api/inventory/purchase-accounts`),
      get(`${API_URL}/api/orders/line-accounts`),
    ]);
    setSalesAccounts(sales?.accounts || []);
    setPurchaseAccounts(purchase?.accounts || []);
    setLineAccounts({
      ready: !!state?.ready,
      reason: state?.ready ? null : (state?.reason || ORDER_LINE_ACCOUNTS_NOT_READY),
      checked: true
    });
  };

  const loadPeriods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/periods`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPeriods(data);

      // Set default to current period (using name now, not id)
      if (data.length > 0) {
        // Find period that contains current date, or default to first
        const now = new Date();
        const currentPeriod = data.find(p => {
          const start = new Date(p.start_date);
          const end = new Date(p.end_date);
          return now >= start && now <= end;
        });
        setSelectedPeriod(currentPeriod ? currentPeriod.name : data[0].name);
      }
    } catch (error) {
      console.error('Error loading periods:', error);
    }
  };

  const loadCustomerPeriods = async (accountId) => {
    try {
      const response = await fetch(`${API_URL}/api/orders/periods/${accountId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPeriods(data);

      // Auto-select current period if available, otherwise first
      if (data.length > 0) {
        const now = new Date();
        const currentPeriod = data.find(p => {
          const start = new Date(p.start_date);
          const end = new Date(p.end_date);
          return now >= start && now <= end;
        });
        setSelectedPeriod(currentPeriod ? currentPeriod.name : data[0].name);
      } else {
        setSelectedPeriod('');
      }
    } catch (error) {
      console.error('Error loading customer periods:', error);
    }
  };

  // Both product lists carry the selected customer's id so each product comes
  // back with its customer links (badged in the picker). Nothing is withheld —
  // Cam, 2026-10-02: a price for one customer must never hide a product from
  // another (see itemCustomerLinkService).
  const loadPreviousItems = async () => {
    if (!selectedCustomerId) { setPreviousItems([]); return; }
    try {
      const res = await fetch(
        `${API_URL}/api/orders/customer-previous-items/${selectedCustomerId}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      setPreviousItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading previous items:', error);
      setPreviousItems([]);
    }
  };

  const loadInventoryItems = async () => {
    try {
      const scope = selectedCustomerId ? `?accountId=${selectedCustomerId}` : '';
      const response = await fetch(`${API_URL}/api/orders/inventory-items${scope}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setInventoryItems(data);
    } catch (error) {
      console.error('Error loading inventory items:', error);
    }
  };

  const loadCustomerContracts = async () => {
    const forKey = pricingKey;
    setLoadingContracts(true);
    try {
      // Include the selected period plus the period each candidate date falls in,
      // so a contract valid for any option date's period is offered.
      const names = new Set();
      if (selectedPeriod) names.add(selectedPeriod);
      candidateDates.forEach(d => { const n = periodNameForDate(d); if (n) names.add(n); });
      const periodNamesParam = [...names].join(',');
      const response = await fetch(
        `${API_URL}/api/orders/customer-contracts/${selectedCustomerId}?periodNames=${encodeURIComponent(periodNamesParam)}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      const arr = Array.isArray(data) ? data : [];
      // Active first, anything retired last — by either flag, since a blend
      // retired here is just as done with as one archived in Xero.
      const done = (c) => (c.is_archived || c.is_locally_archived) ? 1 : 0;
      arr.sort((a, b) => done(a) - done(b));
      setCustomerContracts(arr);
    } catch (error) {
      console.error('Error loading customer contracts:', error);
    } finally {
      setLoadingContracts(false);
      setPricingLoadedFor(p => ({ ...p, contracts: forKey }));
    }
  };

  const loadCustomerSpotPrices = async () => {
    const forKey = pricingKey;
    setLoadingSpotPrices(true);
    try {
      const response = await fetch(
        `${API_URL}/api/orders/customer-spot-prices/${selectedCustomerId}?dates=${encodeURIComponent(candidateDates.join(','))}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setCustomerSpotPrices(data);
    } catch (error) {
      console.error('Error loading customer spot prices:', error);
    } finally {
      setLoadingSpotPrices(false);
      setPricingLoadedFor(p => ({ ...p, spot: forKey }));
    }
  };

  const loadMilkPowderContracts = async () => {
    const forKey = pricingKey;
    try {
      const response = await fetch(
        `${API_URL}/api/orders/customer-milk-powder-contracts/${selectedCustomerId}?dates=${encodeURIComponent(candidateDates.join(','))}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setMilkPowderContracts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading milk powder contracts:', error);
      setMilkPowderContracts([]);
    } finally {
      setPricingLoadedFor(p => ({ ...p, milkPowder: forKey }));
    }
  };

  const loadCustomerDetails = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/orders/customer-details/${selectedCustomerId}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setCustomerDetails(data);
    } catch (error) {
      console.error('Error loading customer details:', error);
    }
  };

  const DELIVERED_STATUSES = ['Delivered', 'Partially Delivered'];

  /**
   * Everything about an order that a person would call a change. Used only to
   * answer one question: has this duplicate been touched at all since it was
   * copied? Line ids and the rest of the UI's bookkeeping are left out — they
   * differ between the copy and the original by construction, and comparing
   * them would report a change that nobody made.
   */
  const signatureOf = ({ customerId, deliveryDate, optionDates, supplierTerms, excelTerms, notes, lines }) =>
    JSON.stringify({
      customerId: customerId || '',
      deliveryDate: deliveryDate || '',
      optionDates: (optionDates || [])
        .filter(o => o.date)
        .map(o => [o.date, Number(o.preference) || 1]),
      supplierTerms: supplierTerms || '',
      excelTerms: excelTerms || '',
      notes: (notes || '').trim(),
      lines: (lines || []).map(l => [
        l.inventory_item_id ?? null,
        l.product_code || '',
        (l.product_name || '').trim(),
        (l.purchase_description || '').trim(),
        Number(l.quantity) || 0,
        Number(l.unit_price) || 0,
        (l.cost_price === '' || l.cost_price === null || l.cost_price === undefined)
          ? null
          : Number(l.cost_price),
        l.tax_type || null,
        l.supplier_contact_id ?? null,
        (l.additives || []).map(a => a.additive_id ?? a.id).sort(),
      ]),
    });

  /** The signature of the form as it stands right now. */
  const currentSignature = () => signatureOf({
    customerId: selectedCustomerId,
    deliveryDate,
    optionDates,
    supplierTerms: supplierDeliveryTerms,
    excelTerms: excelAgriDeliveryTerms,
    notes,
    lines: orderLines,
  });

  const loadOrderForEdit = async (orderId, { duplicate = false } = {}) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/orders/customer-orders/${orderId}`,
        { credentials: 'include' }
      );
      const data = await response.json();

      // Invoiced (Cam, 2026-10-09): open for corrections, like a delivered
      // order — words, item codes, supplier, terms, haulier — with the
      // quantities, prices, tax and lines greyed (the server refuses them).
      // A duplicate starts fresh, as ever.
      const editState = duplicate ? { delivered: false, invoiced: false, any: false } : orderEditState(data);
      setAfterDelivery(editState);

      // Check if order has associated purchase orders. A duplicate inherits
      // none of them — the originals stay with the original order, and this one
      // raises its own after it is saved. Archived POs are the record of what a
      // supplier was once asked for, not something this edit changes.
      const livePOs = duplicate ? [] : (data.purchase_orders || [])
        .filter(po => !['DELETED', 'VOIDED'].includes(String(po.status || '').toUpperCase()));
      setAssociatedPOs(livePOs);
      setHasLinkedPOs(livePOs.length > 0);

      // Delivered, by the same test the server applies (routes/orders.js
      // orderIsDelivered): the date-driven status, or a supplied/delivered
      // quantity already recorded — a stock-only order has no PO to drive the
      // status.
      setCollection(duplicate ? { collectsFromStock: false, collectedDate: null, deliveryStatus: null } : {
        collectsFromStock: !!data.collects_from_stock,
        collectedDate: data.collected_date ? String(data.collected_date).slice(0, 10) : null,
        deliveryStatus: data.delivery_status || null
      });
      setOrderDelivered(!duplicate && (
        DELIVERED_STATUSES.includes(data.delivery_status)
        || editState.invoiced
        || (data.lines || []).some(l => l.supplied_quantity != null || l.delivered_quantity != null)
      ));
      originalQtyRef.current = duplicate
        ? new Map()
        : new Map((data.lines || []).map(l => [l.id, parseFloat(l.quantity) || 0]));
      originalUnitLinesRef.current = duplicate
        ? new Map()
        : new Map((data.lines || []).map(l => [l.id, {
          inventory_item_id: l.inventory_item_id, product_code: l.product_code,
          quantity: l.quantity, unit_weight_kg: l.unit_weight_kg,
          // and what the accounts rule compares (2.34.0)
          product_name: l.product_name, unit_price: l.unit_price, supplier_contact_id: l.supplier_contact_id,
          line_sales_account_code: l.line_sales_account_code || '',
          line_purchase_account_code: l.line_purchase_account_code || ''
        }]));
      editReasonRef.current = '';
      setEditReasonText('');
      setUnitsCorrection(duplicate ? null : (data.units_correction || null));

      // Pre-populate the form with order data
      originalCustomerIdRef.current = duplicate ? '' : data.account_id.toString();
      setSelectedCustomerId(data.account_id.toString());
      setDeliveryDate(data.delivery_date ? data.delivery_date.split('T')[0] : '');
      setOptionDates(Array.isArray(data.option_dates)
        ? data.option_dates.map(o => ({ date: (o.date || '').split('T')[0], preference: o.preference || 1 }))
        : []);
      setSupplierDeliveryTerms(data.supplier_delivery_terms || 'Delivery');
      setExcelAgriDeliveryTerms(data.excel_agri_delivery_terms || 'Delivery');
      setHaulierAccountId(data.haulier_account_id ? String(data.haulier_account_id) : '');
      // A saved service is the user's choice — don't re-suggest over it.
      haulageServiceChosenRef.current = !!data.haulage_service;
      setHaulageService(data.haulage_service || '');
      setNotes(data.notes || '');

      // Convert order lines to the format expected by the form. A duplicated
      // line keeps WHAT was ordered and drops every trace of the order it came
      // from: the line's own id (so the server inserts rather than updates),
      // the PO it was bought on, and whether its price had been accepted.
      const formattedLines = data.lines.map((line, index) => ({
        id: duplicate ? Date.now() + index : line.id,
        contract_id: line.contract_id,
        monthly_pricing_id: line.monthly_pricing_id,
        // Absent before the milk powder link migration: then not sent, so the
        // server leaves it (contractLinkService.milkLinkFor).
        milk_powder_price_id: line.milk_powder_price_id,
        inventory_item_id: line.inventory_item_id,
        product_name: line.product_name,
        // The wording its PO says — stored, or derived for an order saved
        // before the purchase description existed (server: purchase_text).
        purchase_description: line.purchase_text || '',
        product_code: line.product_code,
        quantity: parseFloat(line.quantity),
        unit_price: parseFloat(line.unit_price),
        // The endpoint returns the EFFECTIVE cost — an entered one if there is
        // one, else the derived figure. Showing it either way is right: the
        // cell is a suggestion until someone types over it.
        cost_price: line.cost_price != null ? parseFloat(line.cost_price) : '',
        line_total: parseFloat(line.line_total),
        tax_type: line.tax_type,
        tax_rate: parseFloat(line.tax_rate || 0),
        tax_amount: parseFloat(line.tax_amount || 0),
        additive_id: line.additive_id,
        additive_name: line.additive_name,
        additive_price: line.additive_price ? parseFloat(line.additive_price) : null,
        additives: Array.isArray(line.additives) ? line.additives : [],
        source: line.source,
        contracted_quantity: line.contracted_quantity,
        ordered_quantity: line.ordered_quantity,
        price_accepted: duplicate ? false : (line.price_accepted || false),
        supplier_contact_id: line.supplier_contact_id,
        supplier_name: line.supplier_name,
        supplier_account_no: line.supplier_account_no,
        purchase_order_id: duplicate ? null : line.purchase_order_id,
        purchase_order_number: duplicate ? null : line.purchase_order_number,
        // A typed line's own accounts (2.34.0; absent before the migration).
        line_sales_account_code: line.line_sales_account_code || '',
        line_purchase_account_code: line.line_purchase_account_code || '',
        // Units set on the line (blank = the item's); "Other" reopens as Other.
        ...lineUnitsFromSaved(line),
        // Supplier haulage as saved. A typed rate stays typed; a looked-up one
        // is refreshed for the order as it now stands.
        haulage_rate: line.haulage_rate != null ? Number(line.haulage_rate).toFixed(2) : '',
        haulage_overridden: !!line.haulage_overridden
      }));

      setOrderLines(formattedLines);
      // An edit held while Xero's lock was on (2.32.0): back as it was typed.
      const held = !duplicate ? heldEdits[String(orderId)] : null;
      if (held && Array.isArray(held.lines)) {
        setOrderLines(held.lines);
        const h = held.header || {};
        if (h.deliveryDate !== undefined) setDeliveryDate(h.deliveryDate);
        if (Array.isArray(h.optionDates)) setOptionDates(h.optionDates);
        if (h.supplierDeliveryTerms) setSupplierDeliveryTerms(h.supplierDeliveryTerms);
        if (h.excelAgriDeliveryTerms) setExcelAgriDeliveryTerms(h.excelAgriDeliveryTerms);
        if (h.haulierAccountId !== undefined) setHaulierAccountId(h.haulierAccountId);
        if (h.haulageService !== undefined) setHaulageService(h.haulageService);
        if (h.notes !== undefined) setNotes(h.notes);
        if (held.editReason) { editReasonRef.current = held.editReason; setEditReasonText(held.editReason); }
        setHeldRestored({ orderId: String(orderId), at: held.at || null });
      } else {
        setHeldRestored(null);
      }
      // As loaded, for a units correction after delivery (2.31.0).
      originalLinesRef.current = duplicate || !editState.any
        ? new Map()
        : new Map(formattedLines.map((l, i) => [l.id, {
          unit_weight_kg: l.unit_weight_kg, qty_per_pallet: l.qty_per_pallet, pallets: l.pallets,
          unit_price: l.unit_price, cost_price: l.cost_price, quantity: l.quantity,
          item_unit_weight_kg: data.lines[i]?.item_unit_weight_kg ?? null
        }]));

      if (duplicate) {
        setDuplicatedFrom(data.order_number || null);
        // Taken from the values just loaded rather than from state, which has
        // not re-rendered yet.
        duplicateBaselineRef.current = signatureOf({
          customerId: data.account_id?.toString(),
          deliveryDate: data.delivery_date ? data.delivery_date.split('T')[0] : '',
          optionDates: Array.isArray(data.option_dates)
            ? data.option_dates.map(o => ({ date: (o.date || '').split('T')[0], preference: o.preference || 1 }))
            : [],
          supplierTerms: data.supplier_delivery_terms || 'Delivery',
          excelTerms: data.excel_agri_delivery_terms || 'Delivery',
          notes: data.notes || '',
          lines: formattedLines,
        });
        setAlertDialog({
          isOpen: true,
          type: 'info',
          message: `Copied from ${data.order_number || 'the selected order'}. This will be saved as a NEW order with its own number — check the delivery date and prices before saving.`,
        });
      }
    } catch (error) {
      console.error('Error loading order for edit:', error);
      setAlertDialog({
        isOpen: true,
        message: 'Failed to load order for editing: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  /** A line's purchase wording for the save — never blank (see the payload). */
  const purchaseTextForSave = (line) => {
    const typed = String(line.purchase_description || '').trim();
    if (typed) return typed;
    const item = line.inventory_item_id ? inventoryItems.find(i => i.id === line.inventory_item_id) : null;
    return item ? itemPurchaseTextOf(item) : String(line.product_name || '').trim();
  };

  // Load order from standing order data
  const loadFromStandingOrder = async (data) => {
    /*
     * Never for an archived product (Cam, 15 Oct 2026). The Standing Orders
     * screen refuses first; this re-checks against the standing order as it
     * is now, because the hand-off may be older than an archive. A product
     * that cannot be checked is still caught by the save's own refusal.
     */
    let archivedIds = [];
    if (data.standing_order_id) {
      try {
        const res = await fetch(`${API_URL}/api/standing-orders/${data.standing_order_id}`, { credentials: 'include' });
        const json = await res.json();
        const so = json && json.success ? json.standingOrder : null;
        if (so) {
          archivedIds = (so.products || []).filter(p => p.is_archived).map(p => p.item_id);
          if (so.main_product_archived && so.item_id) archivedIds.push(so.item_id);
        }
      } catch (_) { /* the save checks again */ }
    }
    const archivedLines = archivedLinesOf(data.lines, archivedIds);
    if (archivedLines.length) {
      standingOrderIdRef.current = null;
      setAlertDialog({ isOpen: true, type: 'warning', message: archivedRefusalMessage(archivedLines) });
      return;
    }
    standingOrderIdRef.current = data.standing_order_id || null;

    // Set the customer
    setSelectedCustomerId(data.account_id.toString());

    // Convert standing order lines to order lines format
    const formattedLines = data.lines.map((line, index) => ({
      id: Date.now() + index, // temp ID for UI
      contract_id: null,
      monthly_pricing_id: null,
      milk_powder_price_id: null,
      inventory_item_id: line.item_id || null,
      product_name: line.product_name,
      // The item's purchase wording (else its name); a typed line's own text.
      purchase_description: (() => {
        const item = line.item_id ? inventoryItems.find(i => i.id === line.item_id) : null;
        return item ? itemPurchaseTextOf(item) : (line.product_name || '');
      })(),
      product_code: line.product_code,
      quantity: line.quantity,
      unit_price: line.unit_price || 0,
      line_total: (line.quantity || 0) * (line.unit_price || 0),
      tax_type: null,
      tax_rate: 0,
      tax_amount: 0,
      additive_id: null,
      additive_name: null,
      additive_price: null,
      // The standing order's additives for this product. A free-text one has
      // already been written into product_name by the standing orders screen.
      additives: Array.isArray(line.additives) ? line.additives : [],
      source: 'inventory',
      supplier_contact_id: null,
      supplier_name: line.supplier_name || null,
      supplier_account_no: null,
      price_accepted: false
    }));

    setOrderLines(formattedLines);

    // Show info message — naming any alternative products the standing order
    // accepts, since the line starts with the main one and may need changing.
    const alternatives = (data.alternatives || [])
      .map(p => (p.item_code ? `${p.item_name} (${p.item_code})` : p.item_name))
      .filter(Boolean);
    setAlertDialog({
      isOpen: true,
      message: `Order pre-filled from standing order for ${data.customer_name}. Please review and add any missing details.`
        + (alternatives.length ? ` This standing order also accepts: ${alternatives.join(', ')}.` : '')
        + (data.alternatives_note ? ` ${data.alternatives_note}` : ''),
      type: 'info'
    });
  };

  // Totals are derived from the lines every render by LineTotalsPanel, with the
  // same function the grid's own Tax and Line Total cells use. They used to read
  // stored line_total / tax_amount fields, which only some of the six line
  // builders set — so the footer could disagree with the rows above it.

  const handleSaveOrder = async () => {
    if (!selectedCustomerId) {
      setAlertDialog({ isOpen: true, message: 'Please select a customer', type: 'warning' });
      return;
    }

    if (orderLines.length === 0) {
      setAlertDialog({ isOpen: true, message: 'Please add at least one product line', type: 'warning' });
      return;
    }

    if (qtyRefusals.length || unitRefusals.length || accountRefusals.length) {
      setAlertDialog({ isOpen: true, message: saveRefusalText, type: 'warning' });
      return;
    }

    if (!supplierDeliveryTerms) {
      setAlertDialog({ isOpen: true, message: 'Choose the supplier delivery terms', type: 'warning' });
      return;
    }

    /*
     * Every line needs a supplier, and it has to be chosen.
     *
     * The item used to bring one with it, guessed from its code prefix (see
     * utils/customerPricing) — right often enough that people stopped reading
     * it, and a supplier nobody checked is how the wrong one reaches a purchase
     * order. The guess is gone, so the cell is blank until somebody picks, and
     * this is what stops a blank one being saved. A line the contract named a
     * supplier for arrives already filled and passes without anyone typing.
     */
    const noSupplier = orderLines.filter(line => !lineIsEmpty(line) && !line.supplier_contact_id);
    if (noSupplier.length > 0) {
      const named = noSupplier
        .map(line => line.product_code || line.product_name)
        .filter(Boolean);
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: named.length
          ? `Choose a supplier for ${named.length === 1 ? 'this line' : 'these lines'}: ${named.join(', ')}.`
          : `Choose a supplier for every line (${noSupplier.length} without one).`
      });
      return;
    }

    // An untouched duplicate is far more likely to be a slip than an intention
    // — the delivery date especially, which is almost never the one the copy
    // came with. Ask before it becomes a real order, and before the purchase
    // order prompt that follows it.
    if (duplicatedFrom
        && duplicateBaselineRef.current
        && duplicateBaselineRef.current === currentSignature()) {
      setConfirmDialog({
        isOpen: true,
        message: `Nothing has been changed since this was copied from ${duplicatedFrom} — same customer, same delivery date, same lines. Save it as a second, identical order?`,
        onConfirm: () => {
          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
          // Answered once; don't ask again on a retry of the same save.
          duplicateBaselineRef.current = null;
          handleSaveOrder();
        }
      });
      return;
    }

    // Deccox prescription check — warn if any deccox-containing lines are not covered
    const deccoxLines = orderLines.filter(line =>
      (line.product_name || '').toLowerCase().includes('deccox')
    );
    if (deccoxLines.length > 0) {
      try {
        const resp = await fetch(`${API_URL}/api/prescriptions/check-coverage`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            account_id: parseInt(selectedCustomerId),
            products: deccoxLines.map(l => ({
              product_code: l.product_code || null,
              product_name: l.product_name || null
            }))
          })
        });
        const data = await resp.json();
        if (data.success && data.uncovered && data.uncovered.length > 0) {
          const names = [...new Set(data.uncovered.map(p => p.product_name).filter(Boolean))].join(', ');
          setConfirmDialog({
            isOpen: true,
            message: `This customer does not have a valid prescription for: ${names}. A valid prescription is required for Deccox products. Do you want to continue?`,
            onConfirm: () => {
              setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
              continueSaveAfterDeccoxCheck();
            }
          });
          return;
        }
      } catch (error) {
        console.error('Error checking prescription coverage:', error);
      }
    }

    continueSaveAfterDeccoxCheck();
  };

  /*
   * A delivered order can still be edited; a quantity may not change — what
   * was delivered is recorded with Add Supplied Quantity, which leaves what
   * was ORDERED intact (the server refuses it too, routes/orders.js). The
   * reason, typed in the banner, is optional (2026-10-09).
   */
  const continueSaveAfterDeccoxCheck = () => {
    if (editMode && orderDelivered) {
      const changed = orderLines.filter(line => originalQtyRef.current.has(line.id)
        && (parseFloat(line.quantity) || 0) !== originalQtyRef.current.get(line.id));
      if (changed.length > 0) {
        const named = changed.map(line => line.product_code || line.product_name).filter(Boolean);
        setAlertDialog({
          isOpen: true,
          type: 'warning',
          message: `This order has been ${afterDelivery.invoiced ? 'invoiced' : 'delivered'}, so its quantities are not changed here${named.length ? ` (${named.join(', ')})` : ''}. `
            + 'Record what was actually delivered with Add Supplied Quantity, from the order\'s actions in Search Customer Orders. '
            + 'If it has not been delivered, move its delivery date and save first, then change the quantity.'
        });
        return;
      }
      // The reason is optional now (typed in the banner, if at all).
      editReasonRef.current = editReasonText.trim();
    }
    confirmPurchaseOrderRebuild();
  };

  /*
   * POs already exist against this order. Saving rebuilds them from the lines,
   * so say which, and — for any a supplier already holds — that they will be
   * offered the revised version once the save is done.
   */
  const customerChangedOnEdit = () => editMode && !!originalCustomerIdRef.current
    && !!selectedCustomerId && selectedCustomerId !== originalCustomerIdRef.current;

  const confirmPurchaseOrderRebuild = () => {
    // After delivery the POs are not rebuilt: what may still be corrected
    // reaches them in place, for our records — nothing to confirm or send.
    if (afterDelivery.any) {
      performSaveOrder();
      return;
    }
    if (hasLinkedPOs && associatedPOs.length > 0) {
      const many = associatedPOs.length !== 1;
      const describe = (po) => {
        const status = String(po.status || '').toUpperCase();
        const state = status === 'CONFIRMED' ? 'confirmed by the supplier'
          : po.sent_to_contact ? 'sent to the supplier'
          : 'not yet sent';
        return `${po.purchase_order_number} (${po.contact_name || po.supplier_name || 'supplier'}, ${state})`;
      };
      const held = associatedPOs.filter(po => po.sent_to_contact);
      const customerChanged = customerChangedOnEdit();
      const newCustomerName = customers.find(c => c.id.toString() === selectedCustomerId)?.company_name;
      setConfirmDialog({
        isOpen: true,
        title: many ? 'Update the purchase orders?' : 'Update the purchase order?',
        message: `This order has ${many ? 'purchase orders' : 'a purchase order'}: ${associatedPOs.map(describe).join('; ')}. `
          + `Saving updates ${many ? 'them' : 'it'} to match the lines — items, quantities, units, pallets and delivery date`
          + (customerChanged
            ? `, and the new customer${newCustomerName ? ` (${newCustomerName})` : ''} with their delivery address. `
            : '. ')
          + 'Costs already on a PO are kept unless the product changes (a changed unit re-costs the line per its new unit, '
          + 'the cost per tonne the same); a line moved to another supplier leaves its PO, '
          + 'and a PO left with nothing on it is removed. '
          + (held.length
            ? `${held.map(po => po.purchase_order_number).join(', ')} ${held.length === 1 ? 'is' : 'are'} already with the supplier, `
              + 'so you will be offered to send the revised version once it is saved.'
            : ''),
        confirmText: 'Save',
        onConfirm: () => {
          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
          performSaveOrder();
        }
      });
      return;
    }

    performSaveOrder();
  };

  // The edit as typed, held for this tab while Xero's lock refuses it (2.32.0).
  const holdEdit = () => {
    if (!editMode || !editingOrderId) return;
    setHeldEdits(prev => ({
      ...(prev || {}),
      [String(editingOrderId)]: {
        at: new Date().toISOString(),
        lines: orderLines,
        header: { deliveryDate, optionDates, supplierDeliveryTerms, excelAgriDeliveryTerms, haulierAccountId, haulageService, notes },
        editReason: editReasonRef.current || ''
      }
    }));
  };
  const dropHeldEdit = (id = editingOrderId) => {
    if (!id) return;
    setHeldEdits(prev => {
      if (!prev || !prev[String(id)]) return prev;
      const next = { ...prev };
      delete next[String(id)];
      return next;
    });
  };

  const performSaveOrder = async (saveOpts = {}) => {
    const { confirmCustomerCollection = false, priorMonthConfirmed = false, invoiceChanges = null } = saveOpts;
    // A line must name something. Blank rows are easy to leave behind in a grid
    // that always offers one, and a row with neither an item nor a description
    // is not a line — it is an accident.
    const blanks = orderLines.filter(lineIsEmpty);
    if (blanks.length > 0) {
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: blanks.length === orderLines.length
          ? 'Every line needs either an item or a description before this order can be saved.'
          : `${blanks.length} line${blanks.length > 1 ? 's have' : ' has'} neither an item nor a description. Fill them in, or remove them, then save again.`
      });
      return;
    }

    if (isThirdParty && !haulageService) {
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: `Choose the delivery type (${HAULAGE_SERVICE_OPTIONS.map(o => o.label).join(', ')}) for a Third-Party Delivery order.`
      });
      return;
    }

    if (isThirdParty && !haulierAccountId) {
      setAlertDialog({
        isOpen: true,
        type: 'warning',
        message: 'Choose the third-party haulier before saving a Third-Party Delivery order.'
      });
      return;
    }

    // Bagged / packed lines need pallets on Third-Party Delivery: the haulier
    // is booked by the pallet. Typed, or worked out from a qty per pallet —
    // what the Pallets cell shows. A bulk (per tonne) line, a None line and
    // one of unknown weight need none (Cam, 2026-10-12). The server refuses
    // it too, by the same rule (utils/lineUnits, assertThirdPartyPallets).
    if (isThirdParty) {
      const noPallets = linesMissingPallets(orderLines,
        line => inventoryItems.find(i => i.id === line.inventory_item_id) || null);
      if (noPallets.length > 0) {
        const named = noPallets.map(line => line.product_code || line.product_name).filter(Boolean);
        setAlertDialog({
          isOpen: true,
          type: 'warning',
          message: 'Enter the Pallets for every bagged or packed line of a Third-Party Delivery order — the haulier is booked by the pallet.'
            + (named.length ? ` Missing on: ${named.join(', ')}.` : '')
        });
        return;
      }
    }

    setSaving(true);
    try {
      const url = editMode
        ? `${API_URL}/api/orders/customer-orders/${editingOrderId}`
        : `${API_URL}/api/orders/customer-orders`;

      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: parseInt(selectedCustomerId),
          // The customer the order had when it was opened: a save made after
          // it was diverted is refused rather than moving it back
          // (409 CUSTOMER_CHANGED_SINCE_LOADED).
          ...(editMode && originalCustomerIdRef.current ? { loaded_account_id: parseInt(originalCustomerIdRef.current, 10) } : {}),
          delivery_date: deliveryDate || null,
          option_dates: optionDates.filter(o => o.date),
          supplier_delivery_terms: supplierDeliveryTerms,
          excel_agri_delivery_terms: excelAgriDeliveryTerms,
          haulier_account_id: isThirdParty && haulierAccountId ? parseInt(haulierAccountId, 10) : null,
          haulage_service: isThirdParty ? (haulageService || null) : null,
          notes: notes,
          // Required by the server when the order has been delivered.
          ...(editMode && editReasonRef.current ? { edit_reason: editReasonRef.current } : {}),
          // Yes to "clear the haulier, driver and vehicle" (Customer Collection).
          ...(confirmCustomerCollection ? { confirm_customer_collection: true } : {}),
          // Yes to "dated in a month that should be locked", and the answer to
          // the invoice's units offer (2.32.0).
          ...(priorMonthConfirmed ? { confirm_prior_month: true } : {}),
          ...(invoiceChanges ? { invoice_changes: invoiceChanges } : {}),
          // Made from a standing order: the server refuses an archived product
          // on it (409 STANDING_ORDER_PRODUCT_ARCHIVED, shown as its error).
          ...(!editMode && standingOrderIdRef.current ? { standing_order_id: standingOrderIdRef.current } : {}),
          // Made from a rep's request (or the change it asked): completed
          // with this save, in the same transaction.
          ...(repRequestRef.current ? { rep_request_id: repRequestRef.current.id } : {}),
          lines: orderLines.map(line => ({
            id: line.id, // Matched to the existing line; an unknown id is a new line
            contract_id: line.contract_id,
            monthly_pricing_id: line.monthly_pricing_id,
            milk_powder_price_id: line.milk_powder_price_id,
            category_name: line.category_name,
            inventory_item_id: line.inventory_item_id,
            product_name: line.product_name,
            // Never blank: an emptied box goes back to the item's purchase
            // wording (else its name), or the sale text on a typed line.
            purchase_description: purchaseTextForSave(line),
            product_code: line.product_code,
            quantity: line.quantity,
            unit_price: line.unit_price,
            // Only sent when someone actually typed one. Blank means "use the
            // derived cost", and sending 0 would pin the line to a cost of zero.
            cost_price: (line.cost_price === '' || line.cost_price === null || line.cost_price === undefined)
              ? null
              : parseFloat(line.cost_price),
            tax_type: line.tax_type,
            tax_rate: line.tax_rate,
            tax_amount: lineTotals(line).tax,
            additive_id: line.additive_id,
            additive_name: line.additive_name,
            additive_price: line.additive_price,
            additives: Array.isArray(line.additives) ? line.additives : [],
            supplier_contact_id: line.supplier_contact_id,
            price_accepted: line.price_accepted || false,
            // A typed line's own accounts (2.34.0); the server keeps them only
            // on a line with no item.
            line_sales_account_code: line.line_sales_account_code || null,
            line_purchase_account_code: line.line_purchase_account_code || null,
            // The line's own units; blank = the item's, 0 = None (no weight).
            unit_weight_kg: isNoWeight(line.unit_weight_kg) ? 0 : (line.unit_weight_kg || null),
            qty_per_pallet: line.qty_per_pallet || null,
            pallets: line.pallets === '' || line.pallets == null ? null : line.pallets,
            // Supplier haulage (£/pallet). The server looks the rate up itself;
            // a typed figure is only kept when the line says it was typed.
            haulage_rate: line.haulage_rate === '' || line.haulage_rate == null ? null : line.haulage_rate,
            haulage_overridden: !!line.haulage_overridden,
            // After delivery: a corrected line's answer — what its price was
            // per (the server works the figures out itself, 2.31.0).
            ...(unitsBasisFor(line) ? { units_basis: unitsBasisFor(line) } : {})
          }))
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Saved: an edit held while the lock was on is done with (2.32.0).
        if (editMode) { dropHeldEdit(); setHeldRestored(null); }
        if (result.rep_request && result.rep_request.status === 'completed') {
          showAlert(`${requestRef(result.rep_request.id)} from ${repRequestRef.current?.rep_name || 'the rep'} is done`, 'success');
          repRequestRef.current = null;
        }
        // Something to know about a new order's save (the delivery type Standard
        // before the database holds it is saved as none). An edit's message says it.
        if (!editMode && (result.warnings || []).length) showAlert(`Note: ${result.warnings.join('; ')}`, 'warning');
        // A third-party order goes on to its PO-H once any PO-D is dealt with.
        const haulageNext = result.haulage_po_needed
          ? { orderId: result.order_id || editingOrderId, orderNumber: result.order_number }
          : null;
        setPendingHaulage(haulageNext);

        // A moved delivery date cleared the old day's haulier / driver / vehicle
        // (the server, Cam 19 Oct 2026). The edit's closing message carries it;
        // when something else comes next instead, it is said as a toast.
        const transportCleared = editMode ? (result.transport_cleared?.message || '') : '';

        // What happens after the POs this save changed have been dealt with.
        const continueAfterSave = () => {
          // Show PO generation modal when supplier groups are available — on
          // create, and on an edit for any line not on a PO (a new line, or one
          // moved to a supplier with no PO on this order yet).
          if (transportCleared && ((result.supplier_groups && result.supplier_groups.length > 0) || haulageNext)) {
            showAlert(transportCleared, 'warning');
          }
          if (result.supplier_groups && result.supplier_groups.length > 0) {
            const allSupplierIds = new Set(result.supplier_groups.map(g => String(g.supplier_id)));
            setPoModal({
              isOpen: true,
              orderId: result.order_id || editingOrderId,
              orderNumber: result.order_number,
              supplierGroups: result.supplier_groups,
              selectedSuppliers: allSupplierIds,
              generating: false
            });
          } else if (haulageNext) {
            // Nothing to buy in — straight on to the haulage order.
            offerHaulage(haulageNext);
          } else {
            // No suppliers to generate POs for, just show success and navigate.
            // An edit's message says which PO-D / PO-H followed the change.
            const invoiceNote = invoiceNoteRef.current;
            setAlertDialog({
              isOpen: true,
              message: editMode
                ? `${result.message || 'Order updated successfully'}${invoiceNote?.text ? `. ${invoiceNote.text}` : ''}`
                : `Order ${result.order_number} created successfully`,
              // A note to act on (a billed PO keeps the old item on its bill, a unit kept): a warning.
              type: editMode && (invoiceNote?.type === 'warning' || (result.warnings || []).length || transportCleared) ? 'warning' : 'success'
            });
            setTimeout(() => {
              resetForm();
              leave();
            }, 1500);
          }
        };

        /*
         * An edit may have changed POs a supplier already holds. Each needs
         * telling, and the two cases need different things from the user:
         *   archived — nothing is left on it, so the supplier should cancel it;
         *   updated  — the supplier has the old version, so send the new one
         *              (and, if they had confirmed it, ask them to confirm again).
         * Unsent POs were simply brought into line; nobody else has seen them.
         */
        const changed = Array.isArray(result.po_changes) ? result.po_changes : [];
        const refreshed = Array.isArray(result.haulage_refreshed) ? result.haulage_refreshed : [];
        /*
         * Three kinds of news for suppliers and hauliers, offered in turn:
         *   cancel  — a PO they hold was archived (nothing left on it, the
         *             order left third-party terms, or the PO-H went to a
         *             different haulier): a cancellation, PDF bannered;
         *   revised — a PO they hold changed: the new version, saying what;
         *   new     — a replacement PO-H for a new haulier: the order itself.
         */
        const cancels = [
          ...changed.filter(p => p.outcome === 'archived' && p.sent_to_contact),
          ...refreshed.filter(p => p.outcome === 'cancel')
        ];
        const revised = [
          ...changed.filter(p => p.outcome === 'updated' && p.sent_to_contact),
          ...refreshed.filter(p => p.sent_to_contact && p.outcome !== 'cancel' && p.outcome !== 'new')
        ];
        const fresh = refreshed.filter(p => p.outcome === 'new');
        const asLinked = (p, extra = {}) => ({
          id: p.id,
          purchase_order_number: p.purchase_order_number,
          xero_purchase_order_id: p.xero_purchase_order_id,
          po_type: p.po_type,
          supplier_name: p.supplier_name,
          sent_to_contact: p.sent_to_contact,
          ...extra
        });
        const offerFresh = () => {
          if (!fresh.length) { continueAfterSave(); return; }
          setSendPrompt({
            orderNumber: result.order_number,
            title: 'Send the new haulage order?',
            linkedPos: fresh.map(p => asLinked(p)),
            message: fresh.map(p => `${p.purchase_order_number} is for ${p.supplier_name || 'the new haulier'} (${(p.changes || []).join('; ')}).`).join(' ')
              + ' Send it to them now?',
            next: continueAfterSave
          });
        };
        const offerRevised = () => {
          if (!revised.length) { offerFresh(); return; }
          const confirmed = revised.filter(p => String(p.status || '').toUpperCase() === 'CONFIRMED');
          setSendPrompt({
            orderNumber: result.order_number,
            title: revised.length === 1 ? 'Send the revised purchase order?' : 'Send the revised purchase orders?',
            linkedPos: revised.map(p => asLinked(p, { revision: revisionFor(p.changes) })),
            message: revised.map(p => `${p.purchase_order_number} (${p.supplier_name || 'supplier'}): ${(p.changes || []).join('; ') || 'updated'}.`).join(' ')
              + ` The supplier${revised.length === 1 ? ' has' : 's have'} the previous version. Send the revised one now?`
              + (confirmed.length
                ? ` ${confirmed.map(p => p.purchase_order_number).join(', ')} had been confirmed — ask the supplier to confirm the change.`
                : ''),
            next: offerFresh
          });
        };
        const startOffers = () => {
        if (cancels.length) {
          setSendPrompt({
            orderNumber: result.order_number,
            title: cancels.length === 1 ? 'Send a cancellation?' : 'Send cancellations?',
            linkedPos: cancels.map(p => asLinked(p, {
              cancellation: { reason: (p.changes || []).join('; ') || `no longer needed for ${result.order_number}` }
            })),
            message: cancels.map(p => `${p.purchase_order_number} had been sent to ${p.supplier_name || 'the supplier'} and has been archived`
              + `${(p.changes || []).length ? ` (${p.changes.join('; ')})` : ''}.`).join(' ')
              + ` Email ${cancels.length === 1 ? 'them' : 'each of them'} a cancellation now?`,
            next: offerRevised
          });
        } else {
          offerRevised();
        }
        };
        // An invoiced order corrected: its invoice is offered the matching
        // changes first (never made by itself), then the rest as before.
        const afterCorrected = () => {
          if (editMode && hasInvoiceOffer(result.invoice_offer)) {
            setInvoiceOffer({
              offer: result.invoice_offer,
              orderId: result.order_id || editingOrderId,
              reason: editReasonRef.current || '',
              next: startOffers
            });
          } else {
            startOffers();
          }
        };
        // A corrected invoice that had gone out: offer the customer the
        // corrected copy first (2.33.0).
        if (editMode && Array.isArray(result.corrected_send_offer) && result.corrected_send_offer.length) {
          setCorrectedSend({ offers: result.corrected_send_offer, next: afterCorrected });
        } else {
          afterCorrected();
        }
      } else if (isPriorMonthConfirm(result)) {
        // Dated in a month that should be locked: asked, as any invoice edit asks.
        if (await priorMonth.ask(result)) performSaveOrder({ ...saveOpts, priorMonthConfirmed: true });
      } else if (result.code === UNITS_LOCK_CODE) {
        // Behind Xero's lock: nothing saved, nothing lost — held for this tab.
        holdEdit();
        setLockRefusal({ message: result.error, saveOpts });
      } else if (isInvoiceQuestion(response.status, result)) {
        // The invoice's units correction, offered before anything is saved.
        setUnitsInvoiceQuestion({
          proposals: result.details?.proposals || [],
          notices: result.details?.notices || [],
          stale: !!result.details?.stale,
          saveOpts
        });
      } else if (['REP_REQUEST_DONE', 'REP_REQUEST_MISMATCH', 'REP_REQUEST_NOT_FOUND'].includes(result.code)) {
        /*
         * The rep's request was dealt with meanwhile (or no longer fits this
         * order). Nothing was saved. Saving without it is the person's choice:
         * the order may now be a second one.
         */
        setConfirmDialog({
          isOpen: true,
          title: 'Save without the rep request?',
          message: `${result.error} Nothing has been saved. Save this order without the request — check it isn't a second order for the same thing.`,
          confirmText: 'Save without it',
          cancelText: 'Go back',
          onConfirm: () => {
            setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
            repRequestRef.current = null;
            setRepRequest(null);
            performSaveOrder({ ...saveOpts, confirmCustomerCollection });
          }
        });
      } else if (result.code === 'CUSTOMER_COLLECTION_TRANSPORT') {
        /*
         * Customer Collection with a haulier, driver or vehicle still set.
         * Yes clears them (the old values go in the history) and saves. No
         * saves nothing and leaves the form exactly as it was, the new terms
         * still chosen, to change back, choose another or cancel. The server
         * refuses the save without the answer, so this cannot be skipped.
         */
        setConfirmDialog({
          isOpen: true,
          title: 'Clear the haulier, driver and vehicle?',
          message: `${result.error} Clear them and save?`,
          confirmText: 'Yes, clear and save',
          cancelText: 'No, go back',
          onConfirm: () => {
            setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
            performSaveOrder({ ...saveOpts, confirmCustomerCollection: true });
          }
        });
      } else {
        setAlertDialog({
          isOpen: true,
          message: result.error || (editMode ? 'Failed to update order' : 'Failed to create order'),
          type: 'error'
        });
      }
    } catch (error) {
      console.error(editMode ? 'Error updating order:' : 'Error creating order:', error);
      setAlertDialog({
        isOpen: true,
        message: editMode ? 'Error updating order' : 'Error creating order',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Toggle supplier selection in PO generation modal
  const toggleSupplierSelection = (supplierId) => {
    setPoModal(prev => {
      const newSelected = new Set(prev.selectedSuppliers);
      const id = String(supplierId);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return { ...prev, selectedSuppliers: newSelected };
    });
  };

  // Generate selected POs from the modal
  const handleGenerateSelectedPOs = async () => {
    if (poModal.selectedSuppliers.size === 0) return;

    setPoModal(prev => ({ ...prev, generating: true }));

    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/${poModal.orderId}/create-selective-pos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          supplierIds: Array.from(poModal.selectedSuppliers).map(Number)
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const orderNumber = poModal.orderNumber;
        const created = Array.isArray(result.purchase_orders) ? result.purchase_orders : [];
        setPoModal({ isOpen: false, orderId: null, orderNumber: '', supplierGroups: [], selectedSuppliers: new Set(), generating: false });
        /*
         * Offer to send them, rather than navigating away and leaving the user
         * to find the order again. Answering "not now" does exactly what this
         * did before — the success message and back to the list.
         *
         * The created rows carry a local id, a number and the Xero id, which
         * is what SendPOEmailModal needs; the multi-supplier case never
         * reaches it (SendToSupplierFlow hands those to Search Purchase
         * Orders instead).
         *
         * xero_purchase_order_id is the one that is easy to leave out and
         * hard to miss the loss of: the PDF preview is fetched by it and
         * nothing else, so without it the email composes fine and sends fine
         * and only the preview is blank.
         */
        setSendPrompt({
          orderNumber,
          linkedPos: created.map(po => ({
            id: po.id,
            purchase_order_number: po.number,
            xero_purchase_order_id: po.xero_purchase_order_id,
            sent_to_contact: false
          })),
          message: `Order ${orderNumber} created with ${created.length || result.po_count} purchase order${(created.length || result.po_count) !== 1 ? 's' : ''}. Send ${(created.length || result.po_count) !== 1 ? 'them' : 'it'} to the supplier now?`
        });
      } else {
        setPoModal(prev => ({ ...prev, generating: false }));
        setAlertDialog({
          isOpen: true,
          message: result.error || 'Failed to generate purchase orders',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating POs:', error);
      setPoModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        isOpen: true,
        message: 'Error generating purchase orders',
        type: 'error'
      });
    }
  };

  /*
   * What creating an order has always ended with: say it worked, clear the
   * form, go back to the list. Pulled out so the send prompt can defer it
   * rather than duplicate it.
   */
  const finishAfterSend = (message) => {
    setSendPrompt(null);
    editReasonRef.current = '';
    // The PO-D step is done; a third-party order still has its PO-H to raise.
    if (pendingHaulage) {
      offerHaulage(pendingHaulage);
      return;
    }
    setAlertDialog({ isOpen: true, message, type: 'success' });
    setTimeout(() => {
      resetForm();
      leave();
    }, 1500);
  };

  /*
   * A send prompt is answered — sent, or "not now". One raised by an edit for
   * revised POs carries what comes next (new POs, the PO-H, the success
   * message); one raised on create finishes as creating always has.
   */
  const completeSendPrompt = (prompt) => {
    if (prompt && prompt.next) {
      setSendPrompt(null);
      prompt.next();
      return;
    }
    finishAfterSend(`Order ${prompt?.orderNumber || ''} created`);
  };

  /*
   * Hand an order with several suppliers to Search Purchase Orders.
   *
   * Searching the ORDER number finds them all: a PO raised from a customer
   * order carries that order's number as its reference, which the PO search
   * matches on. Handed over like every "Find in …" (utils/revealInSearch.js),
   * with every filter that could hide one of them cleared.
   */
  const openOrderInPOSearch = (orderNumber) => {
    if (!orderNumber || !onNavigate) return;
    revealInSearch(onNavigate, 'search-purchase-orders', orderNumber, resetForm, {
      tab: 'orders',
      filters: {
        status: 'all_inc_deleted',
        supplier: 'all',
        productRange: [],
        dateType: 'delivery',
        dateFrom: '',
        dateTo: '',
        datePreset: 'all-time',
        page: 1,
        limit: '20',
        includeAdditives: true,
        // Archived POs too: the order's POs are shown wherever they are.
        activeOnly: false
      }
    });
  };

  /*
   * Third-party haulage, step two: raise the PO-H, then offer to send it to the
   * haulier through the same send flow as a PO-D. "Not now" leaves the order
   * saved without one — it can be raised later from Search Customer Orders.
   */
  const offerHaulage = (next) => {
    setPendingHaulage(null);
    const haulier = suppliers.find(s => String(s.id) === String(haulierAccountId));
    setHaulagePrompt({ ...next, haulierName: haulier?.company_name || 'the haulier' });
  };

  const raiseHaulagePO = async () => {
    if (!haulagePrompt) return;
    setRaisingHaulage(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/${haulagePrompt.orderId}/create-haulage-po`, {
        method: 'POST',
        credentials: 'include'
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Failed to raise the haulage order');
      const po = result.purchase_order;
      setHaulagePrompt(null);
      setSendPrompt({
        orderNumber: haulagePrompt.orderNumber,
        title: 'Send to haulier?',
        linkedPos: [{
          id: po.id,
          purchase_order_number: po.number,
          xero_purchase_order_id: po.xero_purchase_order_id,
          sent_to_contact: po.sent_to_contact
        }],
        message: `${result.message}. Send it to ${po.haulier || haulagePrompt.haulierName} now?`
      });
    } catch (error) {
      console.error('Error raising haulage PO:', error);
      setHaulagePrompt(null);
      setAlertDialog({ isOpen: true, message: error.message, type: 'error' });
    } finally {
      setRaisingHaulage(false);
    }
  };

  const skipHaulagePO = () => {
    const orderNumber = haulagePrompt?.orderNumber;
    setHaulagePrompt(null);
    setAlertDialog({
      isOpen: true,
      message: `Order ${orderNumber} saved without a haulage order — raise it from Search Customer Orders (PO-H # column) when ready.`,
      type: 'success'
    });
    setTimeout(() => {
      resetForm();
      leave();
    }, 2500);
  };

  // Skip PO generation and just navigate
  const handleSkipPOGeneration = () => {
    const orderNumber = poModal.orderNumber;
    setPoModal({ isOpen: false, orderId: null, orderNumber: '', supplierGroups: [], selectedSuppliers: new Set(), generating: false });
    if (pendingHaulage) {
      offerHaulage(pendingHaulage);
      return;
    }
    setAlertDialog({
      isOpen: true,
      message: `Order ${orderNumber} created successfully (no POs generated)`,
      type: 'success'
    });
    setTimeout(() => {
      resetForm();
      leave();
    }, 1500);
  };

  const resetForm = () => {
    standingOrderIdRef.current = null;
    repRequestRef.current = null;
    pendingRepLinesRef.current = null;
    setRepRequest(null);
    setAfterDelivery({ delivered: false, invoiced: false, any: false });
    setInvoiceOffer(null);
    invoiceNoteRef.current = null;
    setSelectedCustomerId('');
    setCustomerDetails(null);
    setOrderLines([]);
    setDeliveryDate('');
    setOptionDates([]);
    setSupplierDeliveryTerms('');
    setExcelAgriDeliveryTerms(DEFAULT_OUR_DELIVERY_TERMS);
    setHaulierAccountId('');
    setHaulageService('');
    haulageServiceChosenRef.current = false;
    setPendingHaulage(null);
    setNotes('');
    setCustomerSpotPrices([]);
    setPreviousItems([]);
    setAddItemForRow(null);
    setEditMode(false);
    setEditingOrderId(null);
    setDuplicatedFrom(null);
    duplicateBaselineRef.current = null;
  };

  return (
    <div className="customer-order-container">
      <div className="page-header">
        <div className="workflow-info">
          <span className="info"></span>
          {duplicatedFrom && (
            <span className="note duplicate-note">
              <span className="material-symbols-rounded">content_copy</span>
              <span style={{ margin: 'auto 0' }}>
                Copied from {duplicatedFrom}. Saving creates a new order with its
                own number — no purchase orders have been carried over.
              </span>
            </span>
          )}
        </div>
        <div className="header-actions">
          <button data-read-safe className="btn-round btn-danger" onClick={leave}>
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">Cancel</span>
          </button>
        </div>
      </div>

      <div className="page-scroll-content">
      {/* A rep's request: what they asked, and that saving completes it. */}
      {repRequest && (
        <div className="ror-banner" role="note">
          <span className="ror-banner-title">
            {KIND_WORDS[repRequest.kind]} from {repRequest.rep_name || 'a rep'} — {requestRef(repRequest.id)}
            {repRequest.account_name ? `, ${repRequest.account_name}` : ''}. Saving this order completes it.
          </span>
          {repRequest.kind === 'amend_order' ? (
            <ul>{(amendChanges(repRequest).length ? amendChanges(repRequest) : ['Nothing differs from the order — see the notes']).map((c, i) => <li key={i}>{c}</li>)}</ul>
          ) : (
            <ul>
              {(repRequest.lines || []).map((l, i) => <li key={i}>{lineText(l)}</li>)}
              {repRequest.delivery && <li>{deliveryText(repRequest.delivery)}</li>}
            </ul>
          )}
          {repRequest.notes && <span>“{repRequest.notes}”</span>}
        </div>
      )}
      {/* What editing this order will do beyond the order itself. Nothing is
          locked — the banner says what follows from a change instead. */}
      {editMode && (hasLinkedPOs || orderDelivered) && (
        <>
        {heldRestored && String(heldRestored.orderId) === String(editingOrderId) && (
          <div className="restricted-edit-banner held-edit-banner" role="status">
            <span className="material-symbols-rounded">history</span>
            <div className="banner-content">
              <strong>Your unsaved changes are back</strong>
              <p>
                Saving was stopped by Xero's accounting lock{heldRestored.at ? ` (${new Date(heldRestored.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })})` : ''}.
                Once the lock is removed, press Save again.
              </p>
            </div>
            <div>
              <button type="button" className="btn-slideout" onClick={() => {
                dropHeldEdit();
                setHeldRestored(null);
                loadOrderForEdit(editingOrderId);
              }}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">delete_sweep</span></div>
                <span className="btn-label">Discard them</span>
              </button>
            </div>
          </div>
        )}
        <div className="restricted-edit-banner">
          <span className="material-symbols-rounded">{afterDelivery.invoiced ? 'receipt' : (orderDelivered ? 'local_shipping' : 'link')}</span>
          <div className="banner-content">
            <strong>
              {afterDelivery.invoiced ? 'Invoiced order'
                : (orderDelivered ? (collection.collectedDate && collection.deliveryStatus !== 'Delivered' ? 'Collected by the haulier' : 'Delivered order')
                  : `Purchase order${associatedPOs.length !== 1 ? 's' : ''} raised`)}
            </strong>
            {afterDelivery.any && <p>{orderBannerText(afterDelivery)}</p>}
            {hasLinkedPOs && !afterDelivery.any && (
              <p>
                Saving updates {associatedPOs.map(po => po.purchase_order_number).join(', ')} to match — the customer too, if it is changed.
                {associatedPOs.some(po => po.sent_to_contact)
                  && ' Any a supplier already has, you will be offered to send again.'}
              </p>
            )}
            {orderDelivered && (
              <>
                <p>
                  Every change is kept in the order's history. Record what was delivered with Add Supplied
                  Quantity — quantities are not changed here.
                </p>
                <Textarea
                  className="edit-reason-inline"
                  value={editReasonText}
                  onChange={(e) => setEditReasonText(e.target.value)}
                  placeholder="Reason for the change (optional — kept in the history, and on any purchase order or invoice it reaches)"
                  rows={2}
                  maxLength={500}
                  aria-label="Reason for the change (optional)"
                />
              </>
            )}
          </div>
        </div>
        </>
      )}

      {/* One header row: who, when, and on what terms.
          The customer-detail grid that used to sit here is one click away on the
          eye button, and the recent-orders table now lives inside the Item
          picker — beside the thing it actually informs. Between them they took
          most of the screen above the lines, which is the part of this page
          people are here to fill in. */}
      <div className="section order-header">
        <div className="order-header-row">
          <div
            className="form-group customer-pick"
            title={editMode && afterDelivery.any
              ? (afterDelivery.invoiced
                ? 'Invoiced — its customer is not changed here: credit the invoice and raise the order for the right customer'
                : 'Delivered — to send it to another customer, use Divert (Search Customer Orders or the order window)')
              : undefined}
          >
            <div className="customer-pick-inner">
              <div style={{ flex: 1, minWidth: 0 }}>
                <label>Customer</label>
                <SearchableDropdown
                  autoOpen={autoOpenCustomer}
                  disabled={editMode && afterDelivery.any}
                  value={selectedCustomerId}
                  onChange={(value) => {
                    /*
                     * On an edit the customer changes like anything else: the
                     * lines stay, the new customer's prices are offered (the
                     * same dialog as a date move), and saving carries the
                     * customer to the order's POs and PO-H. Never to nobody.
                     */
                    if (editMode) {
                      if (!value || value === selectedCustomerId) return;
                      const nameOf = (id) => customers.find(c => c.id.toString() === id)?.company_name || '';
                      repricing.noteCustomerChange(selectedCustomerId, value, {
                        fromName: nameOf(selectedCustomerId), toName: nameOf(value)
                      });
                      setSelectedCustomerId(value);
                      return;
                    }
                    // If form has any data, confirm before changing customer
                    const hasFormData = orderLines.length > 0 || deliveryDate || notes || !!supplierDeliveryTerms || excelAgriDeliveryTerms !== DEFAULT_OUR_DELIVERY_TERMS;
                    if (selectedCustomerId && hasFormData && value !== selectedCustomerId) {
                      setConfirmDialog({
                        isOpen: true,
                        message: 'Changing customer will reset the entire form and remove all order lines. Are you sure?',
                        onConfirm: () => {
                          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
                          resetForm();
                          setSelectedCustomerId(value);
                        }
                      });
                    } else {
                      setSelectedCustomerId(value);
                    }
                  }}
                  options={[
                    { value: '', label: 'Select Customer' },
                    ...customers.map(c => {
                      // Parse address to get first line
                      const addressLine = getAddressLine1(c.address);
                      return {
                        value: c.id.toString(),
                        badge: c.account_no || ' ',
                        label: c.company_name + (addressLine ? ` (${addressLine})` : ''),
                        searchText: `${c.account_no || ''} ${c.company_name} ${addressLine}`
                      };
                    })
                  ]}
                  placeholder="Select Customer"
                  searchPlaceholder="Search by # or name..."
                />
              </div>
              {customerDetails && (
                <button
                  className="btn-round view-cust"
                  onClick={() => setShowContactModal(true)}
                  title="View customer details"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">visibility</span>
                  </div>
                </button>
              )}
            </div>
          </div>

            <div className="form-group" title={editMode && afterDelivery.invoiced ? 'Invoiced — the delivery date stays as invoiced' : deliveryFieldsHint}>
              <label>Delivery Date</label>
              <DateInput
                disabled={needsCustomer || (editMode && afterDelivery.invoiced)}
                value={deliveryDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  repricing.noteDateChange(deliveryDate, newDate);
                  setDeliveryDate(newDate);
                  // Auto-update period to match delivery date
                  if (newDate && periods.length > 0) {
                    const d = new Date(newDate);
                    const matchingPeriod = periods.find(p => {
                      const start = new Date(p.start_date);
                      const end = new Date(p.end_date);
                      return d >= start && d <= end;
                    });
                    if (matchingPeriod) {
                      setSelectedPeriod(matchingPeriod.name);
                    }
                  }
                }}
              />
            </div>


            {/* Third-party from our stock: the day the haulier collected it.
                Blank on creation; recorded with Collection / Delivery
                (Search Customer Orders or the order window), never here. */}
            {editMode && excelAgriDeliveryTerms === 'Third-Party Delivery' && (collection.collectedDate || collection.collectsFromStock) && (
              <div className="form-group collected-date-group"
                   title="Recorded with Collection / Delivery once the haulier has collected it — with the quantities collected">
                <label>Collected</label>
                <DateInput value={collection.collectedDate || ''} onChange={() => {}} disabled />
              </div>
            )}

            {/* Option (alternative) delivery dates */}
            <div className="form-group option-dates-group">
              <label>Option Dates</label>
              <div className="option-dates-grid">
                <button
                  type="button"
                  className="btn-round option-date-add"
                  onClick={() => setOptionDates(prev => [...prev, { date: '', preference: prev.length ? Math.max(...prev.map(p => p.preference || 1)) + 1 : 2 }])}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">add</span>
                  </div>
                </button>
                {optionDates.map((od, i) => (
                  <div key={i} className="option-date-row">
                    <DateInput
                      value={od.date}
                      onChange={(e) => setOptionDates(prev => prev.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))}
                    />
                    <div className="option-date-pref">
                      <span style={{ position: 'absolute', top: '7px', left: '9px', textTransform: 'uppercase', fontSize: '0.7em' }}>Opt</span>
                      <input
                        className="styled-input"
                        style={{ padding: '8px 0 0', borderTopLeftRadius: '0', borderBottomLeftRadius: '0', width: '35px', height: '36px' }}
                        type="number"
                        min="1"
                        step="0"
                        value={od.preference}
                        onChange={(e) => setOptionDates(prev => prev.map((x, idx) => idx === i ? { ...x, preference: parseInt(e.target.value, 10) || 1 } : x))}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-icon btn-danger option-date-remove"
                      onClick={() => setOptionDates(prev => prev.filter((_, idx) => idx !== i))}
                      title="Remove option date"
                    >
                      <span className="material-symbols-rounded">close</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Supplier Delivery Terms</label>
              <SearchableDropdown
                disabled={needsCustomer}
                value={supplierDeliveryTerms}
                // Customer Collection on one side sets the other (pairOnChoose);
                // anything else leaves our side as it is.
                onChange={(value) => {
                  const next = pairOnChoose('supplier', value, { supplier: supplierDeliveryTerms, ours: excelAgriDeliveryTerms });
                  setSupplierDeliveryTerms(next.supplier);
                  if (next.ours !== excelAgriDeliveryTerms) setExcelAgriDeliveryTerms(next.ours);
                }}
                options={SUPPLIER_DELIVERY_TERMS_OPTIONS}
                placeholder="Select terms..."
                searchPlaceholder="Search terms..."
              />
            </div>
            <div className="form-group">
              <label>Our Delivery Terms</label>
              <SearchableDropdown
                searchPlaceholder="Search terms..."
                value={excelAgriDeliveryTerms}
                onChange={(value) => {
                  const next = pairOnChoose('ours', value, { supplier: supplierDeliveryTerms, ours: excelAgriDeliveryTerms });
                  setExcelAgriDeliveryTerms(next.ours);
                  if (next.supplier !== supplierDeliveryTerms) setSupplierDeliveryTerms(next.supplier);
                }}
                disabled={needsCustomer || supplierDeliveryTerms === 'Direct Delivery'}
                options={
                  supplierDeliveryTerms === 'Direct Delivery'
                    ? [{ value: 'Direct Delivery', label: 'Direct Delivery' }]
                    : ourDeliveryTermsOptions(excelAgriDeliveryTerms)
                }
              />
            </div>
            {isThirdParty && (
              <div className="form-group">
                <label>Third-Party Haulier</label>
                <SearchableDropdown
                  disabled={needsCustomer}
                  value={haulierAccountId}
                  onChange={(value) => setHaulierAccountId(value || '')}
                  options={[
                    { value: '', label: 'Select haulier...' },
                    ...suppliers.map(s => ({
                      value: String(s.id),
                      label: s.company_name,
                      searchText: `${s.company_name} ${s.account_no || ''} ${s.email || ''}`
                    }))
                  ]}
                  placeholder="Select haulier..."
                  searchPlaceholder="Type a haulier name..."
                />
                {thirdPartyBoardHint && (
                  <div className={`third-party-board-hint ${thirdPartyBoardHint.linked ? 'linked' : ''}`}>
                    <span className="material-symbols-rounded">{thirdPartyBoardHint.linked ? 'local_shipping' : 'info'}</span>
                    <span>{thirdPartyBoardHint.text}</span>
                  </div>
                )}
              </div>
            )}
            {isThirdParty && (
              <div className="form-group">
                <label>Delivery Type</label>
                <div title={(HAULAGE_SERVICE_OPTIONS.find(o => o.value === haulageService) || {}).hint || 'Suggested from the delivery date'}>
                  <Dropdown
                    value={haulageService}
                    onChange={(value) => {
                      haulageServiceChosenRef.current = true;
                      setHaulageService(value);
                    }}
                    options={HAULAGE_SERVICE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                    placeholder="Select type..."
                  />
                </div>
              </div>
            )}
        </div>
      </div>


      {/* Order details — one editable grid. Shown as soon as a customer is
          chosen, because an empty grid with an Add line button is what tells
          you this is where the order gets built. */}
      <div className="section order-lines-section">
          <div className="section-title">Order Details</div>
          <OrderLinesGrid
            lines={orderLines}
            onChange={editMode && afterDelivery.any ? setLinesCorrected : setOrderLines}
            inventoryItems={inventoryItems}
            previousItems={previousItems}
            onRemovePrevious={removePrevious}
            contracts={customerContracts}
            spotPrices={customerSpotPrices}
            milkPowderContracts={milkPowderContracts}
            suppliers={suppliers}
            deliveryDate={deliveryDate}
            periodName={periodNameForDate(deliveryDate)}
            canViewCosts={canViewCosts}
            haulage={isDirectDelivery && canViewCosts ? haulageLookup || { loading: true, rates: {} } : null}
            lockedLineIds={lockedLineIds}
            closed={editMode ? orderClosedCells(afterDelivery, unitsCorrection) : null}
            lineFlags={allLineFlags}
            requireUnits
            requireAccounts={lineAccounts.ready ? (line => accountRefusals.some(r => r.id === line.id)) : false}
            accounts={salesAccounts}
            purchaseAccounts={purchaseAccounts}
            accountsUnavailable={lineAccounts.checked && !lineAccounts.ready ? lineAccounts.reason : null}
            onAddItem={handleOpenAddItem}
            disabled={needsCustomer || needsDeliveryDate}
            disabledHint={lineFieldsHint}
            footerRight={orderLines.length > 0 && <LineTotalsPanel lines={orderLines} />}
          />
        </div>

      {/* Under the lines: what to tell the driver, what we already know about
          this customer, and what it comes to. The notes are the contact's own
          and read-only here — this is the place they are most likely to change
          what someone types to the left of them. */}
      <div className="section order-footer">
          <div className="order-footer-grid">
            <div className="footer-col">
              <label>Delivery Instructions</label>
              <textarea
                className="delivery-instructions"
                disabled={needsCustomer}
                title={deliveryFieldsHint}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — anything the driver needs to know..."
                rows={5}
              />
            </div>

            <div className="footer-col">
              <CustomerNotesPanel
                notes={customerDetails?.notes}
                largestVehicleAccess={customerDetails?.largest_vehicle_access}
              />
            </div>

          </div>
        </div>

      {/* Action Buttons */}
      {orderLines.length > 0 && (
        <div className="form-actions">
          {!hasLinkedPOs && (
            <button
              className="btn-slideout"
              onClick={() => setConfirmDialog({
                isOpen: true,
                title: editMode ? 'Clear and leave this order?' : 'Clear this order?',
                message: editMode
                  ? 'Start a new order instead? Changes to this one that are not saved will be lost.'
                  : 'Clear the customer, dates, delivery terms, notes and every line?',
                confirmText: 'Clear',
                onConfirm: () => {
                  setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
                  resetForm();
                }
              })}
              disabled={saving}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">replay</span>
              </div>
              <span className="btn-label">Clear Order</span>
            </button>
          )}
          <button
            className="btn-slideout"
            onClick={handleSaveOrder}
            disabled={saving || qtyRefusals.length > 0 || unitRefusals.length > 0 || accountRefusals.length > 0}
            title={saveRefusalText || undefined}
          >
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">{saving ? 'progress_activity' : 'save'}</span>
            </div>
            <span className="btn-label">
              {saving
                ? (editMode ? 'Updating...' : 'Creating...')
                : (editMode ? 'Update Order' : 'Create Order')
              }
            </span>
          </button>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && selectedCustomerId && (
        <ContactModal
          contactId={selectedCustomerId}
          onClose={() => setShowContactModal(false)}
        />
      )}


      {/* PO Generation Modal (post-creation) */}
      {poModal.isOpen && createPortal(
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog" style={{ maxWidth: '550px' }}>
            <div className="confirm-dialog-header">
              <span className="material-symbols-rounded" style={{ color: 'var(--color-success)', fontSize: '24px' }}>check_circle</span>
              <h3>Order {poModal.orderNumber} Created</h3>
            </div>
            <div className="confirm-dialog-body">
              <p style={{ marginBottom: 'var(--spacing-md)' }}>
                Select which purchase orders to generate for this order:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                {poModal.supplierGroups.map(group => (
                  <label
                    key={group.supplier_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-sm)',
                      padding: 'var(--spacing-sm) var(--spacing-md)',
                      background: poModal.selectedSuppliers.has(String(group.supplier_id))
                        ? 'rgba(var(--color-success-rgb), 0.08)'
                        : 'var(--bg-tertiary)',
                      border: `1px solid ${poModal.selectedSuppliers.has(String(group.supplier_id))
                        ? 'rgba(var(--color-success-rgb), 0.3)'
                        : 'var(--border-secondary)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={poModal.selectedSuppliers.has(String(group.supplier_id))}
                      onChange={() => toggleSupplierSelection(group.supplier_id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {group.supplier_name}
                      </div>
                      <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>
                        {group.line_count} line{group.line_count !== 1 ? 's' : ''} — {formatCurrency(group.group_total)}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="confirm-dialog-actions">
              <button
                className="btn-slideout"
                onClick={handleSkipPOGeneration}
                disabled={poModal.generating}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Skip</span>
              </button>
              <button
                className="btn-slideout"
                onClick={handleGenerateSelectedPOs}
                disabled={poModal.selectedSuppliers.size === 0 || poModal.generating}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">
                    {poModal.generating ? 'progress_activity' : 'shopping_cart'}
                  </span>
                </div>
                <span className="btn-label">
                  {poModal.generating
                    ? 'Generating...'
                    : `Generate ${poModal.selectedSuppliers.size} PO${poModal.selectedSuppliers.size !== 1 ? 's' : ''}`
                  }
                </span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create an inventory item without leaving a half-built order. The new
          item lands straight on the row that asked for it. */}
      <InventoryItemFormModal
        isOpen={addItemForRow !== null}
        item={null}
        onClose={() => setAddItemForRow(null)}
        onSaved={async (saved) => {
          const rowIndex = addItemForRow;
          setAddItemForRow(null);
          if (!saved) return;
          await loadInventoryItems();
          if (rowIndex === null || rowIndex === undefined) return;
          const resolved = resolveLineForItem({
            item: saved,
            contracts: customerContracts,
            spotPrices: customerSpotPrices,
            milkPowderContracts,
            suppliers,
            deliveryDate,
            periodName: periodNameForDate(deliveryDate),
          });
          setOrderLines(prev => {
            const next = [...prev];
            if (!next[rowIndex]) return prev;
            // The new item's two wordings, as picking it in the grid gives
            // them (utils/pickedItemWords, 2026-10-28).
            const { sales_text: _s, purchase_text: _p, product_name: _n, ...rest } = resolved;
            next[rowIndex] = { ...next[rowIndex], ...rest, ...wordsOnItemPick('order', next[rowIndex], saved) };
            return next;
          });
        }}
      />

      {/* Raised, now send? Answering "Not now" does exactly what creating an
          order did before: the success message, then back to the list. */}
      {sendPrompt && !sendFlow && (
        <ConfirmDialog
          isOpen
          type="info"
          title={sendPrompt.title || 'Send to supplier?'}
          message={sendPrompt.message}
          confirmText="Send now"
          cancelText="Not now"
          onConfirm={() => setSendFlow(sendPrompt)}
          onCancel={() => completeSendPrompt(sendPrompt)}
        />
      )}

      {/* Third-party haulage: raise the PO-H once any PO-D is dealt with. */}
      {haulagePrompt && (
        <ConfirmDialog
          isOpen
          type="info"
          title="Raise haulage order?"
          message={`${haulagePrompt.orderNumber} is on Third-Party Delivery with ${haulagePrompt.haulierName}. Raise its haulage order (PO-H) now?`}
          confirmText={raisingHaulage ? 'Raising...' : 'Raise PO-H'}
          cancelText="Not now"
          onConfirm={raiseHaulagePO}
          onCancel={skipHaulagePO}
        />
      )}

      {sendFlow && (
        <SendToSupplierFlow
          linkedPos={sendFlow.linkedPos}
          orderNumber={sendFlow.orderNumber}
          user={user}
          onSent={(message) => setAlertDialog({ isOpen: true, message, type: 'success' })}
          onOpenInPOSearch={(orderNumber) => {
            openOrderInPOSearch(orderNumber);
            setSendFlow(null);
            setSendPrompt(null);
          }}
          onClose={() => {
            const done = sendFlow;
            setSendFlow(null);
            completeSendPrompt(done);
          }}
        />
      )}

      {invoiceOffer && (
        <InvoiceEditOfferDialog
          offer={invoiceOffer.offer}
          orderId={invoiceOffer.orderId}
          reason={invoiceOffer.reason}
          onDone={(text, type) => {
            const next = invoiceOffer.next;
            invoiceNoteRef.current = text ? { text, type } : null;
            setInvoiceOffer(null);
            next();
          }}
        />
      )}

      {previousItemToast}

      {/* 2.33.0: the corrected copy for the customer, after the save. */}
      {correctedSend && (
        <CorrectedSendDialog
          offers={correctedSend.offers}
          onDone={(text) => {
            const next = correctedSend.next;
            if (text) invoiceNoteRef.current = { text, type: /fail|not sent/i.test(text) ? 'warning' : 'success' };
            setCorrectedSend(null);
            next();
          }}
        />
      )}

      {/* 2.32.0: the invoice's units correction, offered before anything is saved. */}
      {unitsInvoiceQuestion && (
        <InvoiceProposalDialog
          proposals={unitsInvoiceQuestion.proposals}
          notices={unitsInvoiceQuestion.notices}
          stale={unitsInvoiceQuestion.stale}
          busy={saving}
          onContinue={() => setUnitsInvoiceQuestion(null)}
          onApply={() => {
            const q = unitsInvoiceQuestion;
            setUnitsInvoiceQuestion(null);
            performSaveOrder({ ...q.saveOpts, invoiceChanges: applyAnswer(q.proposals) });
          }}
        />
      )}

      {/* 2.32.0: an invoice behind Xero's lock — nothing saved, nothing lost. */}
      {lockRefusal && (
        <ConfirmDialog
          isOpen
          type="warning"
          title="Behind Xero's accounting lock"
          message={`${lockRefusal.message} Your changes are kept here — and if you leave this screen, they come back when you open the order again.`}
          confirmText={checkingLock ? 'Checking…' : 'Check the lock again'}
          cancelText="Close"
          onCancel={() => setLockRefusal(null)}
          onConfirm={async () => {
            if (checkingLock) return;
            setCheckingLock(true);
            try {
              const r = await fetch(`${API_URL}/api/admin/xero-lock/refresh`, { method: 'POST', credentials: 'include' });
              const d = await r.json().catch(() => ({}));
              if (!r.ok) {
                setLockRefusal(null);
                setAlertDialog({ isOpen: true, type: 'warning', message: `The lock could not be checked again: ${d.error || 'Xero did not answer'}. Your changes are still here.` });
                return;
              }
              const opts = lockRefusal.saveOpts;
              setLockRefusal(null);
              performSaveOrder(opts);
            } finally {
              setCheckingLock(false);
            }
          }}
        />
      )}

      {priorMonth.dialog}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}
      />

      <DatePriceChangesDialog {...repricing.dialogProps} dateLabel="delivery date" />
      </div>
    </div>
  );
}

export default CreateCustomerOrder;
